import React, { useState, useEffect, useMemo, useRef } from 'react';
import CreditCardInterface from '../components/CreditCardInterface';
import TraditionalLoanInterface from '../components/TraditionalLoanInterface';
import ResultsSummary from '../components/ResultsSummary';
import EmojiPicker from 'emoji-picker-react';
import { Calculator, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import ToolLayout from '../components/ToolLayout';

// Add styles for drag and drop
const dragStyles = `
  .debt-drag-over {
    border-color: hsl(var(--primary)) !important;
    background-color: hsl(var(--primary) / 0.1) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px hsl(var(--primary) / 0.3);
  }
  
  .debt-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const DebtManagementTest = () => {
  // Backend removed - tool works entirely client-side
  const [showDebtTypeModal, setShowDebtTypeModal] = useState(false);

  // Animation tracking
  const [balanceAnimating, setBalanceAnimating] = useState(false);
  const prevTotalBalance = useRef(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('debt-1');
  const [debts, setDebts] = useState([
    {
      id: 'debt-1',
      name: 'Primary Residence',
      customEmoji: '🏠',
      type: 'traditional-loan',
      data: {
        name: 'Primary Residence',
        principal: 235000,
        current_balance: 235000, // Track current balance separately from original principal
        annual_rate: 0.0675,
        interest_rate_display: '6.75',
        term_months: 360,
        first_payment_date: '2021-09-01',
        county_tax: 0,
        property_insurance: 0,
        pmi: 195,
        additional_monthly_costs: 0,
        custom_minimum_payment: null
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    },
    {
      id: 'debt-2',
      name: 'Honda CR-V',
      customEmoji: '🚗',
      type: 'traditional-loan',
      data: {
        name: 'Honda CR-V',
        principal: 32500,
        current_balance: 32500, // Track current balance separately from original principal
        annual_rate: 0.0749,
        interest_rate_display: '7.49',
        term_months: 72,
        first_payment_date: '2023-02-15',
        county_tax: 0,
        property_insurance: 0,
        pmi: 0,
        additional_monthly_costs: 0,
        custom_minimum_payment: null
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    },
    {
      id: 'debt-3',
      name: 'Visa Credit Card',
      customEmoji: '💳',
      type: 'credit-card',
      data: {
        name: 'Visa Credit Card',
        principal: 7200,
        current_balance: 7200, // Track current balance separately from original principal
        credit_limit: 12500,
        annual_rate: 0.2399,
        interest_rate_display: '23.99',
        minimum_payment_percent: 2.0,
        custom_minimum_payment: null
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      }
    },
    {
      id: 'debt-4',
      name: 'Student Loan',
      customEmoji: '🎓',
      type: 'traditional-loan',
      data: {
        name: 'Student Loan',
        principal: 35000,
        current_balance: 35000, // Track current balance separately from original principal
        annual_rate: 0.0529,
        interest_rate_display: '5.29',
        term_months: 120,
        first_payment_date: '2022-06-01',
        county_tax: 0,
        property_insurance: 0,
        pmi: 0,
        additional_monthly_costs: 0,
        custom_minimum_payment: null
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    },
    {
      id: 'debt-5',
      name: 'Personal Loan',
      customEmoji: '💰',
      type: 'traditional-loan',
      data: {
        name: 'Personal Loan',
        principal: 18500,
        current_balance: 18500, // Track current balance separately from original principal
        annual_rate: 0.1199,
        interest_rate_display: '11.99',
        term_months: 60,
        first_payment_date: '2023-11-01',
        county_tax: 0,
        property_insurance: 0,
        pmi: 0,
        additional_monthly_costs: 0,
        custom_minimum_payment: null
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    }
  ]);
  const [showImportExport, setShowImportExport] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('wahle-tec'); // Default to Wahle-Tec Method
  const [customDebtOrder, setCustomDebtOrder] = useState([]); // Track custom debt ordering
  const [draggedDebt, setDraggedDebt] = useState(null); // Track currently dragged debt
  
  // Inject drag styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = dragStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Debt planning state - Updated with average American household income data
  const [debtPlanningGoals, setDebtPlanningGoals] = useState({
    monthlyIncome: 5200, // Average American monthly take-home pay based on $83,730 median household income
    extraPaymentPercent: 10, // Conservative 10% extra payment allocation
    extraPaymentAmount: 520, // 10% of $5,200 monthly income
    paymentMode: 'percentage', // 'percentage' or 'amount'
    targetDate: '',
    extraBudget: 0,
    selectedStrategy: null
  });
  const [debtPayoffStrategy, setDebtPayoffStrategy] = useState([]);
  

  
  // Modal state for debt customization
  const [showCustomizeModal, setShowCustomizeModal] = useState(null); // Will store debt ID when modal is open
  const [modalDebtName, setModalDebtName] = useState('');
  const [modalDebtEmoji, setModalDebtEmoji] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Handle click outside emoji picker in modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && showCustomizeModal && !event.target.closest('.emoji-picker-react')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker, showCustomizeModal]);

  // Auto-calculate when planning inputs change
  useEffect(() => {
    const hasRequiredData = debtPlanningGoals.monthlyIncome > 0 && 
      ((debtPlanningGoals.paymentMode === 'percentage' && debtPlanningGoals.extraPaymentPercent > 0) ||
       (debtPlanningGoals.paymentMode === 'amount' && debtPlanningGoals.extraPaymentAmount > 0)) &&
      debts.length > 0;
    
    if (hasRequiredData) {
      calculateDebtPayoffStrategy();
    } else {
      setDebtPayoffStrategy([]);
    }
  }, [
    debtPlanningGoals.monthlyIncome,
    debtPlanningGoals.extraPaymentPercent,
    debtPlanningGoals.extraPaymentAmount,
    debtPlanningGoals.paymentMode,
    debtPlanningGoals.selectedStrategy,
    selectedMethod,
    debts,
    customDebtOrder
  ]);

  // Calculate available cash flow after minimum payments
  const calculateAvailableCashFlow = () => {
    if (!debtPlanningGoals.monthlyIncome) return 0;
    
    const totalMinPayments = debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0);
    
    return Math.max(0, debtPlanningGoals.monthlyIncome - totalMinPayments);
  };

  // Helper function to calculate correct minimum payment for any debt
  const calculateMinimumPayment = (debt) => {
    if (debt.type === 'credit-card') {
      const balance = getCurrentBalanceForDebt(debt);
      const rate = debt.data.annual_rate || 0;
      const monthlyInterest = balance * rate / 12;
      
      // Use custom minimum payment if provided, otherwise calculate based on percentage
      if (debt.data.custom_minimum_payment) {
        return debt.data.custom_minimum_payment;
      }
      
      const percentPayment = balance * (debt.data.minimum_payment_percent || 2) / 100;
      return Math.max(percentPayment, monthlyInterest);
    } else {
      // Traditional loan payment calculation with all costs
      
      // Use custom minimum payment if provided
      if (debt.data.custom_minimum_payment) {
        return debt.data.custom_minimum_payment;
      }
      
      const { principal, annual_rate, term_months } = debt.data;
      if (principal > 0 && annual_rate > 0 && term_months > 0) {
        const monthlyRate = annual_rate / 12;
        const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                           (Math.pow(1 + monthlyRate, term_months) - 1);
        const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                              (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
        return basePayment + additionalCosts;
      }
      return 0;
    }
  };

  // Helper function to format time duration
  const formatTimeDuration = (months) => {
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      if (remainingMonths === 0) {
        return `${years} year${years !== 1 ? 's' : ''}`;
      } else {
        return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
      }
    }
  };
  
  // Test data state
  const [loanData, setLoanData] = useState({
    name: 'Test Mortgage',
    principal: 250000,
    annual_rate: 0.045, // 4.5%
    term_months: 360,
    first_payment_date: '2024-02-01',
    county_tax: 208.33,
    property_insurance: 125.00,
    pmi: 104.17
  });
  
  // Additional payments state
  const [additionalPayments, setAdditionalPayments] = useState({
    recurring_extra: 0,
    custom_payments: {} // { payment_number: amount }
  });
  
  // Get current debt
  const getCurrentDebt = () => debts.find(d => d.id === activeTab) || debts[0];
  const currentDebt = getCurrentDebt();

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Function to calculate current balance for any debt
  const getCurrentBalanceForDebt = (debt) => {
    if (!debt || !debt.data) {
      return 0;
    }

    // CRITICAL BUG FIX: Check for current_balance field first
    if (debt.data.current_balance !== undefined && debt.data.current_balance !== null) {
      return debt.data.current_balance;
    }

    // If this debt has a full schedule, calculate current balance based on date
    if (debt.fullSchedule && debt.fullSchedule.length > 0) {
      const today = new Date();
      const firstPaymentDate = new Date(debt.data.first_payment_date || today);
      const monthsDiff = Math.max(0, (today.getFullYear() - firstPaymentDate.getFullYear()) * 12 + 
                        (today.getMonth() - firstPaymentDate.getMonth()));
      
      const currentPaymentNumber = Math.max(1, monthsDiff + 1);
      const currentPayment = debt.fullSchedule.find(p => p.payment_number === currentPaymentNumber);
      
      return currentPayment?.remaining_balance || debt.data.principal || 0;
    }
    
    // For debts without full schedules or current_balance, return original principal
    // NOTE: This is a fallback - ideally all debts should have current_balance tracked
    return debt.data.principal || 0;
  };

  // Animation: track total balance changes
  const totalBalance = useMemo(() => debts.reduce((sum, debt) => sum + getCurrentBalanceForDebt(debt), 0), [debts]);
  useEffect(() => {
    if (prevTotalBalance.current !== null && totalBalance !== prevTotalBalance.current) {
      setBalanceAnimating(true);
      setTimeout(() => setBalanceAnimating(false), 400);
      if (totalBalance < prevTotalBalance.current) {
        const reduced = prevTotalBalance.current - totalBalance;
        if (totalBalance <= 0.01 && prevTotalBalance.current > 0) {
          toast.success('All debts paid off! You are debt-free!');
        } else if (reduced > 1000) {
          toast.success(`Balance reduced by $${Math.round(reduced).toLocaleString()}!`);
        }
      }
    }
    prevTotalBalance.current = totalBalance;
  }, [totalBalance]);

  // Priority calculation functions for different methods
  // Calculate Wahle-Tec Priority Score (CFI-based with portfolio-relative adjustments)
  const calculateWahleTecScore = (debt) => {
    const currentBalance = getCurrentBalanceForDebt(debt);
    if (currentBalance <= 0) return 0;
    
    // Get all debt data for portfolio-relative comparisons
    const allDebts = debts.map(d => {
      const balance = getCurrentBalanceForDebt(d);
      const rate = d.data.annual_rate || 0;
      
      // Calculate monthly payment and months to payoff for each debt
      let monthlyPayment = 0;
      if (d.type === 'credit-card') {
        monthlyPayment = calculateMinimumPayment(d);
      } else {
        const { principal, annual_rate, term_months } = d.data;
        if (principal > 0 && annual_rate > 0 && term_months > 0) {
          const monthlyRate = annual_rate / 12;
          const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                             (Math.pow(1 + monthlyRate, term_months) - 1);
          const additionalCosts = (d.data.county_tax || 0) + (d.data.property_insurance || 0) + 
                                (d.data.pmi || 0) + (d.data.additional_monthly_costs || 0);
          monthlyPayment = basePayment + additionalCosts;
        }
      }
      
      // Calculate months to payoff
      let monthsToPayoff = 0;
      let testBalance = balance;
      const monthlyRate = rate / 12;
      
      while (testBalance > 0.01 && monthsToPayoff < 1000) {
        monthsToPayoff++;
        const interestPayment = testBalance * monthlyRate;
        const principalPayment = Math.max(0, monthlyPayment - interestPayment);
        testBalance -= principalPayment;
        if (principalPayment <= 0) break;
      }
      
      return {
        balance,
        rate,
        monthlyPayment,
        monthsToPayoff,
        cfi: monthsToPayoff > 0 ? (monthlyPayment / monthsToPayoff) : 0,
        type: d.type
      };
    });
    
    // Calculate this debt's metrics
    const thisDebtData = allDebts.find((_, index) => debts[index] === debt);
    if (!thisDebtData) return 0;
    
    // Portfolio statistics for relative comparisons
    const allBalances = allDebts.map(d => d.balance).filter(b => b > 0);
    const allRates = allDebts.map(d => d.rate).filter(r => r > 0);
    const allMonthsToPayoff = allDebts.map(d => d.monthsToPayoff).filter(m => m > 0);
    
    const avgBalance = allBalances.reduce((a, b) => a + b, 0) / allBalances.length;
    const avgRate = allRates.reduce((a, b) => a + b, 0) / allRates.length;
    const avgMonthsToPayoff = allMonthsToPayoff.reduce((a, b) => a + b, 0) / allMonthsToPayoff.length;
    const minRate = Math.min(...allRates);
    const maxRate = Math.max(...allRates);
    const minBalance = Math.min(...allBalances);
    const maxBalance = Math.max(...allBalances);
    
    // Enhanced CFI calculation with interest rate weighting
    // Higher interest rates get exponentially higher base scores
    const interestMultiplier = Math.pow(thisDebtData.rate / minRate, 1.5);
    const enhancedCFI = thisDebtData.cfi * interestMultiplier;
    
    // 1. DANGER LEVEL CATEGORIZATION
    let dangerMultiplier = 1.0;
    if (debt.type === 'credit-card' || thisDebtData.rate >= 0.15) {
      // Extreme danger: Credit cards or rates ≥15%
      dangerMultiplier = 3.0;
    } else if (thisDebtData.rate >= 0.10) {
      // High danger: Rates ≥10%
      dangerMultiplier = 2.0;
    } else if (thisDebtData.rate >= 0.07) {
      // Moderate danger: Rates ≥7%
      dangerMultiplier = 1.5;
    } else if (thisDebtData.rate <= 0.05) {
      // Safe debt: Rates ≤5% (like good mortgages)
      dangerMultiplier = 0.3;
    }
    
    // 2. LOW INTEREST PENALTY (Heavily penalize safe debt like mortgages)
    let lowInterestPenalty = 0;
    const rateRatio = thisDebtData.rate / avgRate;
    if (rateRatio < 0.6) {
      // Significantly below average rate gets major penalty
      lowInterestPenalty = enhancedCFI * (0.6 - rateRatio) * 2.0;
    } else if (rateRatio < 0.8) {
      // Moderately below average rate gets minor penalty
      lowInterestPenalty = enhancedCFI * (0.8 - rateRatio) * 1.0;
    }
    
    // 3. ENHANCED QUICK WIN BOOST (Favor smaller balances more aggressively)
    let quickWinBoost = 0;
    const balanceRatio = currentBalance / avgBalance;
    if (balanceRatio < 0.4) {
      // Very small balance gets major boost
      quickWinBoost = enhancedCFI * (0.4 - balanceRatio) * 1.5;
    } else if (balanceRatio < 0.7) {
      // Small balance gets moderate boost
      quickWinBoost = enhancedCFI * (0.7 - balanceRatio) * 1.0;
    }
    
    // 4. HIGH INTEREST AMPLIFICATION (Extra boost for high rates)
    let highInterestBoost = 0;
    if (thisDebtData.rate > avgRate) {
      const rateExcess = (thisDebtData.rate - avgRate) / avgRate;
      highInterestBoost = enhancedCFI * rateExcess * 1.2;
    }
    
    // 5. PAYOFF SPEED CONSIDERATION
    let payoffSpeedBoost = 0;
    if (thisDebtData.monthsToPayoff < avgMonthsToPayoff * 0.8) {
      const speedAdvantage = (avgMonthsToPayoff - thisDebtData.monthsToPayoff) / avgMonthsToPayoff;
      payoffSpeedBoost = enhancedCFI * speedAdvantage * 0.8;
    }
    
    // WAHLE-TEC ENHANCED SCORE CALCULATION
    const baseScore = enhancedCFI * dangerMultiplier;
    const adjustedScore = baseScore + highInterestBoost + quickWinBoost + payoffSpeedBoost - lowInterestPenalty;
    
    // Ensure minimum score for active debts
    const finalScore = Math.max(adjustedScore, 0.1);
    
    return finalScore;
  };

  const getAvalancheScore = (debt) => {
    return debt.data.annual_rate || 0; // Higher interest rate = higher score
  };

  const getSnowballScore = (debt) => {
    // CRITICAL BUG FIX: Use getCurrentBalanceForDebt instead of original principal
    const currentBalance = getCurrentBalanceForDebt(debt);
    return -currentBalance; // Negative because smaller balance = higher priority (higher score)
  };

  const getCFIScore = (debt) => {
    const currentBalance = getCurrentBalanceForDebt(debt);
    
    // Calculate monthly minimum payment
    let monthlyPayment = 0;
    if (debt.type === 'credit-card') {
      monthlyPayment = calculateMinimumPayment(debt);
    } else {
      // Traditional loan payment calculation
      const { principal, annual_rate, term_months } = debt.data;
      if (principal > 0 && annual_rate > 0 && term_months > 0) {
        const monthlyRate = annual_rate / 12;
        const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                           (Math.pow(1 + monthlyRate, term_months) - 1);
        const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                              (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
        monthlyPayment = basePayment + additionalCosts;
      }
    }
    
    // Calculate months to payoff with minimum payments only
    let monthsToPayoff = 0;
    let balance = currentBalance;
    const monthlyRate = (debt.data.annual_rate || 0) / 12;
    
    while (balance > 0.01 && monthsToPayoff < 1000) {
      monthsToPayoff++;
      const interestPayment = balance * monthlyRate;
      const principalPayment = Math.max(0, monthlyPayment - interestPayment);
      balance -= principalPayment;
      if (principalPayment <= 0) break; // Prevent infinite loop if payment doesn't cover interest
    }
    
    // CFI = Monthly Payment ÷ Months to Payoff (higher is better)
    return monthsToPayoff > 0 ? (monthlyPayment / monthsToPayoff) : 0;
  };

  // Initialize custom debt order based on current method selection
  const initializeCustomOrder = (previousMethod = 'wahle-tec') => {
    // Temporarily switch to previous method to get sorted order
    const tempSortedDebts = [...debts];
    switch (previousMethod) {
      case 'wahle-tec':
        tempSortedDebts.sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
        break;
      case 'avalanche':
        tempSortedDebts.sort((a, b) => getAvalancheScore(b) - getAvalancheScore(a));
        break;
      case 'snowball':
        tempSortedDebts.sort((a, b) => getSnowballScore(b) - getSnowballScore(a));
        break;
      case 'cfi':
        tempSortedDebts.sort((a, b) => getCFIScore(b) - getCFIScore(a));
        break;
      default:
        tempSortedDebts.sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
        break;
    }
    const currentOrder = tempSortedDebts.map(debt => debt.id);
    setCustomDebtOrder(currentOrder);
  };

  // Drag and drop handlers for custom debt ordering
  const handleDebtDragStart = (e, debtId) => {
    setDraggedDebt(debtId);
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'debt',
      debtId: debtId
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDebtDragEnd = (e) => {
    setDraggedDebt(null);
    // Remove any drag-over styling
    document.querySelectorAll('.debt-drag-over').forEach(el => {
      el.classList.remove('debt-drag-over');
    });
  };

  const handleDebtDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback
    const debtCard = e.target.closest('.debt-card');
    if (debtCard && !debtCard.classList.contains('debt-drag-over')) {
      document.querySelectorAll('.debt-drag-over').forEach(el => {
        el.classList.remove('debt-drag-over');
      });
      debtCard.classList.add('debt-drag-over');
    }
  };

  const handleDebtDrop = (e, targetDebtId) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
    
    if (dragData.type === 'debt' && dragData.debtId !== targetDebtId) {
      const newOrder = [...customDebtOrder];
      const draggedIndex = newOrder.indexOf(dragData.debtId);
      const targetIndex = newOrder.indexOf(targetDebtId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Remove dragged item and insert at new position
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, dragData.debtId);
        setCustomDebtOrder(newOrder);
        
        // Force recalculation of debt payoff strategy with new order
        // This will trigger the useEffect that calls calculateDebtPayoffStrategy
        setTimeout(() => {
          // The useEffect will automatically recalculate when customDebtOrder changes
        }, 100);
      }
    }
    
    // Remove drag-over styling
    document.querySelectorAll('.debt-drag-over').forEach(el => {
      el.classList.remove('debt-drag-over');
    });
  };

  const getSortedDebts = () => {
    const sortedDebts = [...debts];
    
    switch (selectedMethod) {
      case 'wahle-tec':
        return sortedDebts.sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
      case 'avalanche':
        return sortedDebts.sort((a, b) => getAvalancheScore(b) - getAvalancheScore(a));
      case 'snowball':
        return sortedDebts.sort((a, b) => getSnowballScore(b) - getSnowballScore(a));
      case 'cfi':
        return sortedDebts.sort((a, b) => getCFIScore(b) - getCFIScore(a));
      case 'custom':
        // Return debts in custom order if available, otherwise maintain current order
        if (customDebtOrder.length > 0) {
          const orderedDebts = [];
          customDebtOrder.forEach(debtId => {
            const debt = sortedDebts.find(d => d.id === debtId);
            if (debt) orderedDebts.push(debt);
          });
          // Add any debts not in custom order to the end
          sortedDebts.forEach(debt => {
            if (!orderedDebts.find(d => d.id === debt.id)) {
              orderedDebts.push(debt);
            }
          });
          return orderedDebts;
        }
        return sortedDebts;
      default:
        return sortedDebts;
    }
  };

  const getMethodInfo = () => {
    switch (selectedMethod) {
      case 'wahle-tec':
        return {
          title: 'Enhanced Wahle-Tec Method™',
          description: 'Our recommended approach. Sorts your debts by how dangerous they are — credit cards go first, low-rate loans like mortgages go last. Balances size, interest rate, and how fast you can free up cash to attack the next debt.',
          color: 'purple'
        };
      case 'avalanche':
        return {
          title: 'Traditional Avalanche',
          description: 'Pay the minimum on everything, then throw extra money at the highest interest rate first. Saves the most money over time.',
          color: 'red'
        };
      case 'snowball':
        return {
          title: 'Traditional Snowball',
          description: 'Pay the minimum on everything, then throw extra money at the smallest balance first. You pay off debts faster, which feels great and keeps you going.',
          color: 'blue'
        };
      case 'cfi':
        return {
          title: 'Cash Flow Index (CFI)', 
          description: 'Pays off the debts that free up the most monthly cash the fastest. Great if your main goal is to have more breathing room in your budget each month.',
          color: 'green'
        };
      case 'custom':
        return {
          title: 'Custom Prioritization',
          description: 'You decide the order. Drag and drop your debts into whatever payoff order makes sense for you.',
          color: 'teal'
        };
      default:
        return {
          title: 'Select a Method',
          description: 'Pick how you want to order your debts.',
          color: 'gray'
        };
    }
  };

  const getCurrentLoanStatus = () => {
    if (!currentDebt || !currentDebt.data) {
      return null;
    }
    
    // Default values if fullSchedule is not available
    const defaultStatus = {
      currentPaymentNumber: 1,
      currentBalance: currentDebt.data.principal || 0,
      totalPayments: currentDebt.data.term_months || 360,
      monthsRemaining: currentDebt.data.term_months || 360,
      originalPrincipal: currentDebt.data.principal || 0,
      paymentsMade: 0,
      remainingPayments: currentDebt.data.term_months || 360,
      totalInterestPaid: 0,
      projectedPayoffDate: 'N/A',
      fullSchedule: []
    };
    
    if (!currentDebt.fullSchedule || !currentDebt.fullSchedule.length) {
      return defaultStatus;
    }
    
    const today = new Date();
    const firstPaymentDate = new Date(currentDebt.data.first_payment_date || today);
    const monthsDiff = (today.getFullYear() - firstPaymentDate.getFullYear()) * 12 + 
                      (today.getMonth() - firstPaymentDate.getMonth());
    
    const currentPaymentNumber = Math.max(1, monthsDiff + 1);
    const currentPayment = currentDebt.fullSchedule.find(p => p.payment_number === currentPaymentNumber);
    
    return {
      currentPaymentNumber,
      currentBalance: currentPayment?.remaining_balance || currentDebt.data.principal || 0,
      totalPayments: currentDebt.fullSchedule.length,
      monthsRemaining: currentDebt.fullSchedule.length - currentPaymentNumber + 1,
      originalPrincipal: currentDebt.data.principal || 0,
      paymentsMade: currentPaymentNumber - 1,
      remainingPayments: currentDebt.fullSchedule.length - currentPaymentNumber + 1,
      totalInterestPaid: 0,
      projectedPayoffDate: currentDebt.fullSchedule[currentDebt.fullSchedule.length - 1]?.date || 'N/A',
      fullSchedule: currentDebt.fullSchedule
    };
  };

  // Calculate comprehensive debt payoff strategy
  const calculateDebtPayoffStrategy = () => {
    if (!debtPlanningGoals.monthlyIncome || debtPlanningGoals.monthlyIncome <= 0 || debts.length === 0) {
      setDebtPayoffStrategy([]);
      return;
    }
    
    // Calculate extra payment amount
    let monthlyExtraPayment = 0;
    if (debtPlanningGoals.paymentMode === 'percentage') {
      if (!debtPlanningGoals.extraPaymentPercent || debtPlanningGoals.extraPaymentPercent <= 0) {
        setDebtPayoffStrategy([]);
        return;
      }
      monthlyExtraPayment = debtPlanningGoals.monthlyIncome * debtPlanningGoals.extraPaymentPercent / 100;
    } else {
      if (!debtPlanningGoals.extraPaymentAmount || debtPlanningGoals.extraPaymentAmount <= 0) {
        setDebtPayoffStrategy([]);
        return;
      }
      monthlyExtraPayment = debtPlanningGoals.extraPaymentAmount;
    }

    // Determine which method to use for calculations
    // CRITICAL FIX: Planning tab selection should override Analysis tab when explicitly set
    let methodToUse;
    
    if (debtPlanningGoals.selectedStrategy) {
      // Planning tab method selection takes highest priority when set
      methodToUse = debtPlanningGoals.selectedStrategy;
    } else if (selectedMethod === 'custom') {
      // Use custom method from Analysis tab only if no Planning tab override
      methodToUse = 'custom';
    } else {
      // Fall back to Analysis tab selection
      methodToUse = selectedMethod;
    }
    
    // Sort debts using the specific method (not defaulting to selectedMethod)
    const sortedOriginalDebts = [...debts];
    switch (methodToUse) {
      case 'wahle-tec':
        sortedOriginalDebts.sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
        break;
      case 'avalanche':
        sortedOriginalDebts.sort((a, b) => getAvalancheScore(b) - getAvalancheScore(a));
        break;
      case 'snowball':
        sortedOriginalDebts.sort((a, b) => getSnowballScore(b) - getSnowballScore(a));
        break;
      case 'cfi':
        sortedOriginalDebts.sort((a, b) => getCFIScore(b) - getCFIScore(a));
        break;
      case 'custom':
        // Use custom debt order if available
        if (customDebtOrder.length > 0) {
          const orderedDebts = [];
          customDebtOrder.forEach(debtId => {
            const debt = sortedOriginalDebts.find(d => d.id === debtId);
            if (debt) orderedDebts.push(debt);
          });
          // Add any debts not in custom order to the end
          sortedOriginalDebts.forEach(debt => {
            if (!orderedDebts.find(d => d.id === debt.id)) {
              orderedDebts.push(debt);
            }
          });
          sortedOriginalDebts.length = 0;
          sortedOriginalDebts.push(...orderedDebts);
        } else {
          // CRITICAL FIX: If custom method is selected but no order is set yet,
          // initialize it with Wahle-Tec order and use that immediately
          const wahleTecSorted = [...sortedOriginalDebts].sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
          const newCustomOrder = wahleTecSorted.map(debt => debt.id);
          setCustomDebtOrder(newCustomOrder);
          
          // Use the Wahle-Tec order for this calculation
          sortedOriginalDebts.length = 0;
          sortedOriginalDebts.push(...wahleTecSorted);
        }
        break;
      default:
        // Default to Wahle-Tec if no method specified
        sortedOriginalDebts.sort((a, b) => calculateWahleTecScore(b) - calculateWahleTecScore(a));
        break;
    }

    // Now map the sorted original debts to working debts in the correct order
    const workingDebts = sortedOriginalDebts.map(debt => {
      const currentBalance = getCurrentBalanceForDebt(debt);
      let monthlyMinimum = 0;
      
      if (debt.type === 'credit-card') {
        monthlyMinimum = calculateMinimumPayment(debt);
      } else if (debt.type === 'traditional-loan' || debt.type === 'loan' || debt.type === 'mortgage') {
        // Traditional loan payment calculation
        const { principal, annual_rate, term_months } = debt.data;
        if (principal > 0 && annual_rate > 0 && term_months > 0) {
          const monthlyRate = annual_rate / 12;
          const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                             (Math.pow(1 + monthlyRate, term_months) - 1);
          const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                                (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
          monthlyMinimum = basePayment + additionalCosts;
        }
      }
      
      return {
        ...debt,
        balance: currentBalance,
        originalBalance: currentBalance,
        monthlyMinimum: monthlyMinimum,
        totalInterestPaid: 0,
        totalPaid: 0,
        payoffMonth: 0,
        isComplete: false
      };
    });

    // Simulate month-by-month payoff
    let currentMonth = 0;
    let availableExtraPayment = monthlyExtraPayment;
    
    while (workingDebts.some(debt => !debt.isComplete) && currentMonth < 1000) { // Safety limit
      currentMonth++;
      
      // Apply minimum payments to all active debts
      workingDebts.forEach(debt => {
        if (debt.isComplete) return;
        
        const monthlyRate = (debt.data.annual_rate || 0) / 12;
        const interestPayment = debt.balance * monthlyRate;
        const principalPayment = Math.min(debt.monthlyMinimum - interestPayment, debt.balance);
        
        debt.totalInterestPaid += interestPayment;
        debt.totalPaid += interestPayment + principalPayment;
        debt.balance -= principalPayment;
        
        if (debt.balance <= 0.01) {
          debt.balance = 0;
          debt.isComplete = true;
          debt.payoffMonth = currentMonth;
          // Add this debt's minimum payment to available extra payment
          availableExtraPayment += debt.monthlyMinimum;
        }
      });
      
      // Apply extra payment to highest priority active debt
      const targetDebt = workingDebts.find(debt => !debt.isComplete);
      if (targetDebt && availableExtraPayment > 0) {
        const extraPrincipal = Math.min(availableExtraPayment, targetDebt.balance);
        targetDebt.balance -= extraPrincipal;
        targetDebt.totalPaid += extraPrincipal;
        
        if (targetDebt.balance <= 0.01) {
          targetDebt.balance = 0;
          targetDebt.isComplete = true;
          targetDebt.payoffMonth = currentMonth;
          // DO NOT add minimum payment here - already added above when debt is completed
        }
      }
    }

    // Convert to strategy format
    const strategy = workingDebts.map((debt, index) => {
      // Calculate priority score for display using original debt object
      let priorityScore = 0;
      // Find the original debt object that matches this working debt
      const originalDebt = debts.find(d => d.id === debt.id);
      
      if (methodToUse === 'avalanche') {
        priorityScore = (debt.data.annual_rate || 0) * 100;
      } else if (methodToUse === 'snowball') {
        priorityScore = debt.originalBalance > 0 ? (1000000 / debt.originalBalance) : 0;
      } else if (methodToUse === 'cfi') {
        priorityScore = debt.originalBalance > 0 ? (debt.monthlyMinimum / debt.originalBalance) : 0;
      } else if (methodToUse === 'custom') {
        // For custom prioritization, use the index as priority score (higher index = lower priority)
        // We reverse it so #1 priority has highest score
        priorityScore = workingDebts.length - index;
      } else {
        // Use original debt object for Wahle-Tec score calculation
        priorityScore = originalDebt ? calculateWahleTecScore(originalDebt) : 0;
      }

      // Calculate cumulative extra payment for this debt (snowball effect)
      let cumulativeExtraPayment = monthlyExtraPayment; // Start with initial extra payment
      // Add minimum payments from all previously paid off debts
      for (let i = 0; i < index; i++) {
        cumulativeExtraPayment += workingDebts[i].monthlyMinimum;
      }
      
      return {
        id: debt.id,
        name: debt.data.name || `Debt ${index + 1}`,
        type: debt.type,
        startingBalance: debt.originalBalance,
        interestRate: (debt.data.annual_rate || 0) * 100,
        priorityScore: priorityScore,
        monthlyPayment: debt.monthlyMinimum,
        extraPayment: cumulativeExtraPayment, // Show cumulative extra payment
        totalMonthlyPayment: debt.monthlyMinimum + cumulativeExtraPayment,
        payoffMonth: debt.payoffMonth,
        totalInterestPaid: debt.totalInterestPaid,
        totalPaid: debt.totalPaid,
        method: methodToUse
      };
    });

    setDebtPayoffStrategy(strategy);
  };



  // Print Debt Analysis
  const printDebtAnalysis = () => {
    if (debts.length === 0) {
      alert('No debts available to analyze');
      return;
    }

    const methodInfo = getMethodInfo();
    const sortedDebts = getSortedDebts();
    const totalBalance = debts.reduce((sum, debt) => sum + getCurrentBalanceForDebt(debt), 0);
    const avgRate = totalBalance > 0 ? debts.reduce((sum, debt) => {
      const balance = getCurrentBalanceForDebt(debt);
      return sum + ((debt.data.annual_rate || 0) * balance);
    }, 0) / totalBalance : 0;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Debt Analysis Report - ${methodInfo.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #333; padding-bottom: 20px; }
            .method-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 25px; }
            .debt-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            .debt-table th, .debt-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .debt-table th { background-color: #f8f9fa; font-weight: bold; }
            .debt-table .priority-1 { background-color: #fee2e2; }
            .debt-table .priority-2 { background-color: #fef3cd; }
            .debt-table .priority-3 { background-color: #d1fae5; }
            .summary { background: #f8f9fa; padding: 20px; border-radius: 8px; }
            .recommendation { background: #fef2f2; padding: 15px; border-left: 5px solid #ef4444; margin-top: 20px; }
            .print-date { text-align: right; color: #666; font-size: 12px; margin-bottom: 20px; }
            @media print { .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="print-date">Generated on ${new Date().toLocaleDateString()}</div>
          
          <div class="header">
            <h1>🎯 Multi-Debt Efficiency Analysis</h1>
            <h2>${methodInfo.title}</h2>
          </div>
          
          <div class="method-info">
            <h3>📊 Analysis Method: ${methodInfo.title}</h3>
            <p>${methodInfo.description}</p>
          </div>
          
          <h3>💰 Portfolio Summary</h3>
          <table class="debt-table">
            <tr>
              <td><strong>Total Debts:</strong></td>
              <td>${debts.length}</td>
              <td><strong>Total Balance:</strong></td>
              <td>$${formatCurrency(totalBalance)}</td>
            </tr>
            <tr>
              <td><strong>Average Rate:</strong></td>
              <td>${(avgRate * 100).toFixed(2)}%</td>
              <td><strong>Total Min Payments (Monthly):</strong></td>
              <td>${formatCurrency(debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0))}</td>
            </tr>
            <tr>
              <td><strong>Total Paid if Minimum Payments Only:</strong></td>
              <td>${formatCurrency((() => {
                return debts.reduce((sum, debt) => {
                  const monthlyPayment = calculateMinimumPayment(debt);
                  if (debt.type === 'credit-card') {
                    // For credit cards, estimate payoff time and total paid
                    const balance = getCurrentBalanceForDebt(debt);
                    const rate = debt.data.annual_rate || 0;
                    if (balance > 0 && rate > 0 && monthlyPayment > balance * rate / 12) {
                      // Calculate months to pay off with minimum payment
                      const monthlyRate = rate / 12;
                      const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                      return sum + (monthlyPayment * Math.ceil(months));
                    }
                    return sum + balance; // Fallback if calculation fails
                  } else {
                    // For traditional loans, use original term
                    const term = debt.data.term_months || 360;
                    return sum + (monthlyPayment * term);
                  }
                }, 0);
              })())}</td>
            </tr>
            <tr>
              <td><strong>Total Interest if Minimum Payments Only:</strong></td>
              <td>${formatCurrency((() => {
                return debts.reduce((sum, debt) => {
                  const monthlyPayment = calculateMinimumPayment(debt);
                  const currentBalance = getCurrentBalanceForDebt(debt);
                  if (debt.type === 'credit-card') {
                    // For credit cards, estimate total interest
                    const balance = getCurrentBalanceForDebt(debt);
                    const rate = debt.data.annual_rate || 0;
                    if (balance > 0 && rate > 0 && monthlyPayment > balance * rate / 12) {
                      const monthlyRate = rate / 12;
                      const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                      const totalPaid = monthlyPayment * Math.ceil(months);
                      return sum + (totalPaid - balance);
                    }
                    return sum + (balance * 0.5); // Rough estimate if calculation fails
                  } else {
                    // For traditional loans
                    const term = debt.data.term_months || 360;
                    const totalPaid = monthlyPayment * term;
                    return sum + (totalPaid - currentBalance);
                  }
                }, 0);
              })())}</td>
            </tr>
          </table>
          
          <h3>🎯 Debt Priority Analysis</h3>
          <table class="debt-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Debt Name</th>
                <th>Type</th>
                <th>Current Balance</th>
                <th>Interest Rate</th>
                <th>${selectedMethod === 'wahle-tec' ? 'Priority Score' : 
                     selectedMethod === 'avalanche' ? 'Interest Rate' : 
                     selectedMethod === 'snowball' ? 'Balance Size' : 'CFI Score'}</th>
                <th>Recommendation</th>
              </tr>
            </thead>
            <tbody>
              ${sortedDebts.map((debt, index) => {
                const currentBalance = getCurrentBalanceForDebt(debt);
                const rate = debt.data.annual_rate || 0;
                let score;
                switch (selectedMethod) {
                  case 'wahle-tec':
                    score = calculateWahleTecScore(debt).toFixed(1);
                    break;
                  case 'avalanche':
                    score = (getAvalancheScore(debt) * 100).toFixed(2) + '%';
                    break;
                  case 'snowball':
                    score = '$' + formatCurrency(Math.abs(getSnowballScore(debt)));
                    break;
                  case 'cfi':
                    score = '$' + getCFIScore(debt).toFixed(2);
                    break;
                  default:
                    score = '0';
                }
                
                const priorityClass = index === 0 ? 'priority-1' : index === 1 ? 'priority-2' : 'priority-3';
                const recommendation = index === 0 ? 'PAY FIRST' : index === 1 ? 'NEXT PRIORITY' : 'PAY MINIMUM';
                
                return `
                  <tr class="${priorityClass}">
                    <td><strong>#${index + 1}</strong></td>
                    <td>${debt.data.name}</td>
                    <td>${debt.type === 'credit-card' ? 'Credit Card' : 'Traditional Loan'}</td>
                    <td>$${formatCurrency(currentBalance)}</td>
                    <td>${(rate * 100).toFixed(2)}%</td>
                    <td>${score}</td>
                    <td><strong>${recommendation}</strong></td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="recommendation">
            <h3>🚨 Key Recommendation</h3>
            <p><strong>Focus on ${sortedDebts[0]?.data.name || 'your highest priority debt'} first!</strong></p>
            <p>Apply any extra payments to this debt while maintaining minimum payments on all others. This ${methodInfo.title.toLowerCase()} approach will ${
              selectedMethod === 'wahle-tec' ? 'provide optimal balance between mathematical efficiency and psychological wins' :
              selectedMethod === 'avalanche' ? 'minimize your total interest payments' :
              selectedMethod === 'snowball' ? 'build momentum through quick psychological victories' :
              'maximize your monthly cash flow recovery speed'
            }.</p>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #666;">
            <p><strong>Generated by:</strong> Wahle-Tec Debt Management & Optimization Tool</p>
            <p><strong>Analysis Date:</strong> ${new Date().toLocaleDateString()}</p>
            <p><strong>Method Used:</strong> ${methodInfo.title}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 100);
  };

  // Print Debt Plan
  const printDebtPlan = () => {
    if (debtPayoffStrategy.length === 0) {
      alert('Please complete your debt planning inputs first');
      return;
    }

    const methodInfo = getMethodInfo();
    const totalMonthsToFreedom = Math.round(debtPayoffStrategy.reduce((max, debt) => Math.max(max, debt.payoffMonth), 0));
    const totalInterestPaid = debtPayoffStrategy.reduce((sum, debt) => sum + debt.totalInterestPaid, 0);
    const finalCashflowRecovery = debtPayoffStrategy.reduce((sum, debt) => sum + debt.monthlyPayment, 0);

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Debt Payoff Plan - ${methodInfo.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #333; padding-bottom: 20px; }
            .strategy-summary { background: #f0f9ff; padding: 20px; border-radius: 8px; margin-bottom: 25px; border: 2px solid #0369a1; }
            .timeline-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            .timeline-table th, .timeline-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            .timeline-table th { background-color: #f8f9fa; font-weight: bold; }
            .debt-step { margin-bottom: 20px; padding: 15px; border-left: 5px solid #6366f1; background: #f8fafc; }
            .success-highlight { background: #dcfce7; border: 2px solid #16a34a; padding: 20px; border-radius: 8px; margin-top: 25px; }
            .print-date { text-align: right; color: #666; font-size: 12px; margin-bottom: 20px; }
            @media print { .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="print-date">Generated on ${new Date().toLocaleDateString()}</div>
          
          <div class="header">
            <h1>📊 Comprehensive Debt Payoff Strategy</h1>
            <h2>Your Path to Financial Freedom</h2>
          </div>
          
          <div class="strategy-summary">
            <h3>🎯 Your Strategy: ${methodInfo.title}</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-top: 15px;">
              <div>
                <strong>Monthly Income:</strong> $${formatCurrency(debtPlanningGoals.monthlyIncome)}<br>
                <strong>Extra Payment:</strong> $${formatCurrency(debtPlanningGoals.paymentMode === 'percentage' ? 
                  debtPlanningGoals.monthlyIncome * debtPlanningGoals.extraPaymentPercent / 100 : 
                  debtPlanningGoals.extraPaymentAmount)}<br>
                <strong>Payment Mode:</strong> ${debtPlanningGoals.paymentMode === 'percentage' ? 
                  `${debtPlanningGoals.extraPaymentPercent}% of income` : 'Fixed amount'}
              </div>
              <div>
                <strong>Time to Freedom:</strong> ${formatTimeDuration(totalMonthsToFreedom)}<br>
                <strong>Total Interest:</strong> $${formatCurrency(totalInterestPaid)}<br>
                <strong>Monthly Cashflow Recovery:</strong> $${formatCurrency(finalCashflowRecovery)}/month
              </div>
            </div>
          </div>
          
          <h3>📅 Detailed Payoff Timeline</h3>
          <table class="timeline-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Debt Name</th>
                <th>Current Balance</th>
                <th>Interest Rate</th>
                <th>Total Payment</th>
                <th>Payoff Month</th>
                <th>Interest Paid</th>
              </tr>
            </thead>
            <tbody>
              ${debtPayoffStrategy.map((debt, index) => `
                <tr>
                  <td><strong>#${index + 1}</strong></td>
                  <td>${debt.name}</td>
                  <td>$${formatCurrency(debt.startingBalance)}</td>
                  <td>${debt.interestRate.toFixed(2)}%</td>
                  <td>$${formatCurrency(debt.totalMonthlyPayment)}</td>
                  <td>${formatTimeDuration(debt.payoffMonth - (index > 0 ? debtPayoffStrategy[index-1].payoffMonth : 0))}</td>
                  <td>$${formatCurrency(debt.totalInterestPaid)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${debtPayoffStrategy.map((debt, index) => `
            <div class="debt-step">
              <h4>Step ${index + 1}: ${debt.name}</h4>
              <p><strong>Strategy:</strong> ${debt.method === 'wahle-tec' ? 
                `Priority Score ${debt.priorityScore.toFixed(1)} (Balanced Efficiency)` :
                debt.method === 'avalanche' ? 
                `Interest Rate ${debt.interestRate.toFixed(2)}% (Highest First)` :
                debt.method === 'snowball' ? 
                `Balance $${formatCurrency(debt.startingBalance)} (Smallest First)` :
                `CFI Score ${debt.priorityScore.toFixed(2)} (Cash Flow Recovery Speed)`
              }</p>
              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 10px;">
                <div><strong>Minimum:</strong> $${formatCurrency(debt.monthlyPayment)}</div>
                <div><strong>Extra Applied:</strong> $${formatCurrency(debt.extraPayment)}</div>
                <div><strong>Total Monthly:</strong> $${formatCurrency(debt.totalMonthlyPayment)}</div>
              </div>
              <p style="margin-top: 10px;"><strong>Result:</strong> Paid off in ${formatTimeDuration(debt.payoffMonth - (index > 0 ? debtPayoffStrategy[index-1].payoffMonth : 0))}, freeing up $${formatCurrency(debt.monthlyPayment)}/month for the next debt.</p>
            </div>
          `).join('')}
          
          <div class="success-highlight">
            <h3>🎉 Your Debt Freedom Achievement</h3>
            <p><strong>Congratulations!</strong> Following this ${methodInfo.title.toLowerCase()} strategy, you will be completely debt-free in <strong>${formatTimeDuration(totalMonthsToFreedom)}</strong>.</p>
            <p><strong>Monthly Cash Flow Recovery:</strong> $${formatCurrency(finalCashflowRecovery)} per month will be available for wealth building, emergency fund, or pursuing your financial dreams!</p>
            <p><strong>Wealth Building Potential:</strong> This represents ${(finalCashflowRecovery / debtPlanningGoals.monthlyIncome * 100).toFixed(1)}% of your current take-home pay that will be freed up for building your future.</p>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #666;">
            <p><strong>Generated by:</strong> Wahle-Tec Debt Management & Optimization Tool</p>
            <p><strong>Plan Date:</strong> ${new Date().toLocaleDateString()}</p>
            <p><strong>Strategy Method:</strong> ${methodInfo.title}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 100);
  };
  
  // Results state
  const [loanResult, setLoanResult] = useState(null);
  const [amortizationResult, setAmortizationResult] = useState(null);

  // Initialize backend URL
  // Backend initialization removed - tool works entirely offline

  // Health check removed - tool works entirely offline

  const calculateTotalMonthlyPayment = () => {
    const { principal, annual_rate, term_months } = loanData;
    const monthlyRate = annual_rate / 12;
    const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                       (Math.pow(1 + monthlyRate, term_months) - 1);
    
    const totalAdditionalCosts = loanData.county_tax + loanData.property_insurance + loanData.pmi;
    return basePayment + totalAdditionalCosts + additionalPayments.recurring_extra;
  };

  const generateFullSchedule = (debtData, additionalPayments) => {
    const { principal, annual_rate, term_months, first_payment_date } = debtData;
    if (principal <= 0 || annual_rate < 0 || term_months <= 0) return [];
    
    const monthlyRate = annual_rate / 12;
    const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                       (Math.pow(1 + monthlyRate, term_months) - 1);
    
    let remainingBalance = principal;
    const schedule = [];
    const startDate = new Date(first_payment_date);
    
    for (let paymentNum = 1; paymentNum <= term_months && remainingBalance > 0.01; paymentNum++) {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(startDate.getMonth() + paymentNum - 1);
      
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = Math.min(basePayment - interestPayment, remainingBalance);
      
      // Get additional payment for this month
      const additionalPrincipal = (additionalPayments.custom_payments[paymentNum] || 0) + 
                                  additionalPayments.recurring_extra;
      const totalAdditionalPrincipal = Math.min(additionalPrincipal, remainingBalance - principalPayment);
      
      const totalPayment = basePayment + totalAdditionalPrincipal;
      remainingBalance -= (principalPayment + totalAdditionalPrincipal);
      
      if (remainingBalance < 0.01) {
        remainingBalance = 0;
      }
      
      schedule.push({
        payment_number: paymentNum,
        date: paymentDate.toLocaleDateString(),
        payment_amount: totalPayment,
        interest: interestPayment,
        principal: principalPayment,
        additional_principal: totalAdditionalPrincipal,
        remaining_balance: remainingBalance
      });
      
      if (remainingBalance <= 0) break;
    }
    
    return schedule;
  };

  const updateCurrentDebt = (updates) => {
    setDebts(prev => prev.map(debt => 
      debt.id === activeTab 
        ? { ...debt, ...updates }
        : debt
    ));
  };

  const updateDebtData = (newData) => {
    const updates = { data: { ...currentDebt.data, ...newData } };
    
    // CRITICAL BUG FIX: When principal is updated, also update current_balance to match
    // This assumes the user is editing the current balance, not creating a new debt
    if (newData.principal !== undefined && newData.current_balance === undefined) {
      updates.data.current_balance = newData.principal;
    }
    
    // If name is being updated, also update the tab name
    if (newData.name) {
      updates.name = newData.name;
    }
    
    updateCurrentDebt(updates);
  };

  const updateAdditionalPayment = (paymentNumber, amount) => {
    const numAmount = parseFloat(amount) || 0;
    const newCustomPayments = { ...currentDebt.additionalPayments.custom_payments };
    if (numAmount > 0) {
      newCustomPayments[paymentNumber] = numAmount;
    } else {
      delete newCustomPayments[paymentNumber];
    }
    
    updateCurrentDebt({
      additionalPayments: {
        ...currentDebt.additionalPayments,
        custom_payments: newCustomPayments
      }
    });
  };

  const addNewDebt = () => {
    setShowDebtTypeModal(true);
  };

  const deleteDebt = (debtId) => {
    // Prevent deleting the last debt
    if (debts.length <= 1) {
      alert('Cannot delete the last debt. You must have at least one debt.');
      return;
    }

    // Confirm deletion
    const debtToDelete = debts.find(d => d.id === debtId);
    if (!debtToDelete) return;

    const confirmed = window.confirm(`Are you sure you want to delete "${debtToDelete.name}"? This action cannot be undone.`);
    if (!confirmed) return;

    // Remove the debt from the array
    const updatedDebts = debts.filter(d => d.id !== debtId);
    setDebts(updatedDebts);

    // If the deleted debt was the active tab, switch to the first available debt
    if (activeTab === debtId) {
      setActiveTab(updatedDebts[0].id);
    }
  };

  // Handle opening customize modal
  const openCustomizeModal = (debt) => {
    setShowCustomizeModal(debt.id);
    setModalDebtName(debt.name || debt.data.name || '');
    setModalDebtEmoji(debt.customEmoji || (debt.type === 'credit-card' ? '💳' : '🏠'));
    setShowEmojiPicker(false);
  };

  // Handle saving modal changes
  const saveCustomization = () => {
    if (!modalDebtName.trim()) {
      alert('Debt name cannot be empty');
      return;
    }

    setDebts(prev => prev.map(debt => 
      debt.id === showCustomizeModal 
        ? { 
            ...debt, 
            name: modalDebtName.trim(),
            customEmoji: modalDebtEmoji,
            data: { ...debt.data, name: modalDebtName.trim() }
          }
        : debt
    ));

    closeCustomizeModal();
  };

  // Handle closing modal
  const closeCustomizeModal = () => {
    setShowCustomizeModal(null);
    setModalDebtName('');
    setModalDebtEmoji('');
    setShowEmojiPicker(false);
  };

  // Handle emoji selection in modal
  const handleModalEmojiClick = (emojiObject, event) => {
    console.log('Emoji clicked:', emojiObject); // Debug log
    const emoji = emojiObject.emoji || emojiObject.native || emojiObject.unified;
    console.log('Extracted emoji:', emoji);
    setModalDebtEmoji(emoji);
    setShowEmojiPicker(false);
  };



  const createTraditionalLoan = () => {
    const newId = `loan-${Date.now()}`;
    const newDebt = {
      id: newId,
      name: `New Loan ${debts.length + 1}`,
      customEmoji: '🏠', // Default house emoji
      type: 'loan',
      data: {
        name: `New Loan ${debts.length + 1}`,
        principal: 0,
        current_balance: 0, // Track current balance separately from original principal
        annual_rate: 0.05,
        term_months: 360,
        first_payment_date: new Date().toISOString().split('T')[0],
        county_tax: 0,
        property_insurance: 0,
        pmi: 0,
        additional_monthly_costs: 0,
        interest_rate_display: '5.0'
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    };
    
    setDebts(prev => [...prev, newDebt]);
    setActiveTab(newId);
    setShowDebtTypeModal(false);
  };

  const createCreditCard = () => {
    const newId = `credit-card-${Date.now()}`;
    const newDebt = {
      id: newId,
      name: `Credit Card ${debts.filter(d => d.type === 'credit-card').length + 1}`,
      customEmoji: '💳', // Default credit card emoji
      type: 'credit-card',
      data: {
        name: `Credit Card ${debts.filter(d => d.type === 'credit-card').length + 1}`,
        principal: 0,
        current_balance: 0, // Track current balance separately from original principal
        credit_limit: 5000,
        annual_rate: 0.1899,
        interest_rate_display: '18.99',
        minimum_payment_percent: 2,
        custom_minimum_payment: null,
        first_payment_date: new Date().toISOString().split('T')[0]
      },
      additionalPayments: {
        recurring_extra: 0,
        custom_payments: {}
      },
      fullSchedule: []
    };
    
    setDebts(prev => [...prev, newDebt]);
    setActiveTab(newId);
    setShowDebtTypeModal(false);
  };

  // Regenerate schedule when debt data changes
  useEffect(() => {
    if (currentDebt) {
      const schedule = generateFullSchedule(currentDebt.data, currentDebt.additionalPayments);
      updateCurrentDebt({ fullSchedule: schedule });
    }
  }, [currentDebt?.data, currentDebt?.additionalPayments]);

  const calculateLoanPayment = () => {
    // Removed backend dependency - calculations are client-side
    
    const { principal, annual_rate, term_months } = loanData;
    const monthlyRate = annual_rate / 12;
    const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                       (Math.pow(1 + monthlyRate, term_months) - 1);
    
    const totalMonthlyPayment = calculateTotalMonthlyPayment();
    const totalPayments = totalMonthlyPayment * term_months;
    const totalInterest = (basePayment * term_months) - principal;
    
    setLoanResult({
      base_payment: basePayment.toFixed(2),
      total_monthly_payment: totalMonthlyPayment.toFixed(2),
      total_payments: totalPayments.toFixed(2),
      total_interest: totalInterest.toFixed(2)
    });
  };

  const createLoanAndGetAmortization = () => {
    // Removed backend dependency - calculations are client-side
    setLoading(true);
    
    try {
      // Calculate amortization client-side - no backend needed
      const schedule = generateFullSchedule(loanData, additionalPayments);
      
      // Calculate summary
      const totalPayments = schedule.reduce((sum, p) => sum + p.payment_amount, 0);
      const totalInterest = schedule.reduce((sum, p) => sum + p.interest, 0);
      const lastPayment = schedule[schedule.length - 1];
      const payoffDate = lastPayment ? lastPayment.date : new Date().toLocaleDateString();
      
      const amortization = {
        loan: {
          name: loanData.name || 'Loan',
          principal: loanData.principal,
          annual_rate: loanData.annual_rate,
          term_months: loanData.term_months
        },
        schedule: schedule,
        summary: {
          total_payments: totalPayments.toFixed(2),
          total_interest: totalInterest.toFixed(2),
          payoff_date: payoffDate
        }
      };
      
      setAmortizationResult(amortization);
      
    } catch (error) {
      console.error('Amortization calculation failed:', error);
      alert('Error calculating amortization: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    const exportData = {
      debts: debts,
      export_date: new Date().toISOString(),
      version: "2.2",
      planning_data: {
        monthlyIncome: debtPlanningGoals.monthlyIncome,
        extraPaymentPercent: debtPlanningGoals.extraPaymentPercent,
        extraPaymentAmount: debtPlanningGoals.extraPaymentAmount,
        paymentMode: debtPlanningGoals.paymentMode,
        selectedStrategy: debtPlanningGoals.selectedStrategy || selectedMethod
      },
      debt_payoff_strategy: debtPayoffStrategy,
      custom_debt_order: customDebtOrder
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debt-management-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Debt data saved successfully!');
  };

  const handleImportData = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);
        if (importedData.debts && Array.isArray(importedData.debts)) {
          setDebts(importedData.debts);

          if (importedData.planning_data) {
            setDebtPlanningGoals(prev => ({
              ...prev,
              monthlyIncome: importedData.planning_data.monthlyIncome || 0,
              extraPaymentPercent: importedData.planning_data.extraPaymentPercent || 0,
              extraPaymentAmount: importedData.planning_data.extraPaymentAmount || 0,
              paymentMode: importedData.planning_data.paymentMode || 'percentage',
              selectedStrategy: importedData.planning_data.selectedStrategy || null
            }));
            if (importedData.planning_data.selectedStrategy) {
              setSelectedMethod(importedData.planning_data.selectedStrategy);
            }
          }

          if (importedData.debt_payoff_strategy) {
            setDebtPayoffStrategy(importedData.debt_payoff_strategy);
          }

          if (importedData.custom_debt_order && Array.isArray(importedData.custom_debt_order)) {
            setCustomDebtOrder(importedData.custom_debt_order);
          }

          const details = [
            'Data loaded successfully!',
            importedData.planning_data ? ' Planning configuration restored.' : '',
            importedData.debt_payoff_strategy ? ' Payoff strategy restored.' : ''
          ].join('');
          toast.success(details);
        } else {
          toast.error('Invalid file format. Please select a valid saved JSON file.');
        }
      } catch (error) {
        toast.error('Error reading file. Please select a valid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const renderTabButton = (tabId, label, icon) => (
    <button
      key={tabId}
      onClick={() => setActiveTab(tabId)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
        activeTab === tabId
          ? 'bg-primary text-primary-foreground border-primary border-b-2 shadow-sm'
          : 'bg-card text-text-primary border-border hover:bg-surface-muted hover:border-primary/50'
      }`}
    >
      {typeof icon === 'string' ? (
        <span className="text-lg leading-none">{icon}</span>
      ) : (
        <span className="flex items-center justify-center w-6 h-6 flex-shrink-0">{icon}</span>
      )}
      <span>{label}</span>
    </button>
  );

  const renderDebtTabButton = (debt) => (
    <div key={debt.id} className="relative group">
      <button
        onClick={() => setActiveTab(debt.id)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
          activeTab === debt.id
            ? 'bg-primary text-primary-foreground border-primary border-b-2 shadow-sm'
            : 'bg-card text-text-primary border-border hover:bg-surface-muted hover:border-primary/50'
        }`}
      >
        <span className="text-lg leading-none">{debt.customEmoji || (debt.type === 'credit-card' ? '💳' : '🏠')}</span>
        <span>{debt.name}</span>
      </button>
      
      {/* Delete button - only show if more than 1 debt exists */}
      {debts.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteDebt(debt.id);
          }}
          className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full text-xs hover:opacity-90 transition-opacity opacity-0 group-hover:opacity-100 z-10"
          title={`Delete ${debt.name}`}
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <ToolLayout
      title="Debt Management Tool"
      description="Find the best way to pay off your debts. Add your debts, pick a payoff method, and see how much you can save."
      icon={Calculator}
      guide={{
        intro: 'Figure out the best order to pay off your debts.',
        steps: [
          'Add each debt (credit card or loan) with balance, interest rate, and minimum payment.',
          'Pick a payoff method or let us recommend one.',
          'Set goals and try different "what if" scenarios.',
          'Drag to reorder debts if you want.',
        ],
      }}
    >
    <div>
        <div className="mb-8">
        {/* Save/Load Buttons */}
        <div className="flex items-center justify-end gap-2 mb-4">
          <button
            onClick={handleExportData}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 shadow-sm"
          >
            <Download size={18} />
            Save
          </button>
          <label className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 cursor-pointer shadow-sm">
            <Upload size={18} />
            Load
            <input
              type="file"
              accept=".json"
              onChange={(e) => {
                handleImportData(e.target.files[0]);
                e.target.value = '';
              }}
              className="hidden"
            />
          </label>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap items-center">
          {/* Individual Debt Tabs */}
          {debts.map((debt) => renderDebtTabButton(debt))}
          
          {/* Visual separator before actions */}
          <div className="w-px h-8 bg-border flex-shrink-0" aria-hidden="true" />
          
          {/* Add New Debt */}
          <button
            onClick={addNewDebt}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium border border-dashed border-primary bg-primary/10 text-primary hover:bg-primary/20 hover:border-primary transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <span className="text-lg leading-none">➕</span>
            <span>Add New Debt</span>
          </button>
          
          {/* Utility Tabs */}
          {renderTabButton('debt-efficiency', 'Debt Analysis', '🎯')}
          {renderTabButton('planning', 'Planning', '📊')}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Individual Debt Tabs */}
        {debts.some(debt => debt.id === activeTab) && (
          <div>
            {/* Render appropriate interface based on debt type */}
            {currentDebt && currentDebt.type === 'credit-card' ? (
              <CreditCardInterface 
                currentDebt={currentDebt}
                updateDebtData={updateDebtData}
                updateCurrentDebt={updateCurrentDebt}
                formatCurrency={formatCurrency}
                openCustomizeModal={openCustomizeModal}
              />
            ) : (
              <TraditionalLoanInterface
                currentDebt={currentDebt}
                updateDebtData={updateDebtData}
                updateCurrentDebt={updateCurrentDebt}
                formatCurrency={formatCurrency}
                getCurrentLoanStatus={getCurrentLoanStatus}
                calculateMinimumPayment={calculateMinimumPayment}
                generateFullSchedule={generateFullSchedule}
                openCustomizeModal={openCustomizeModal}
              />
            )}

            {/* Results Summary for Traditional Loans */}
            <ResultsSummary
              currentDebt={currentDebt}
              generateFullSchedule={generateFullSchedule}
              formatCurrency={formatCurrency}
            />
          </div>
        )}

        {/* Debt Efficiency Tab */}
        {activeTab === 'debt-efficiency' && (
          <div className="space-y-6">
            <div className="bg-surface-muted p-6 rounded-lg border border-border">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-text-primary">
                  🎯 Multi-Debt Efficiency Analysis
                </h2>
                <button
                  onClick={printDebtAnalysis}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium"
                >
                  🖨️ Print Analysis
                </button>
              </div>
              
              {/* Debt Overview Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="text-2xl font-bold text-primary">
                    {debts.length}
                  </div>
                  <div className="text-sm text-text-muted">Total Debts</div>
                </div>
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className={`text-2xl font-bold text-text-primary ${balanceAnimating ? 'animate-bounce-scale' : ''}`}>
                    ${formatCurrency(totalBalance)}
                  </div>
                  <div className="text-sm text-text-muted">Total Balance</div>
                </div>
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="text-2xl font-bold text-text-primary">
                    {((debts.reduce((sum, debt) => sum + (debt.data.annual_rate || 0), 0) / debts.length) * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-text-muted">Avg Interest Rate</div>
                </div>
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="text-2xl font-bold text-primary">
                    ${formatCurrency(debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0))}
                  </div>
                  <div className="text-sm text-text-muted">Total Min Payments</div>
                </div>
              </div>

              {/* Method Selection and Analysis */}
              <div className="bg-card p-6 rounded-lg border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-text-primary">
                    🎯 Debt Prioritization Analysis
                  </h3>
                  <div className="text-xs text-text-muted bg-surface-muted px-2 py-1 rounded border border-border">
                    Real-time Analysis as of {new Date().toLocaleDateString()}
                  </div>
                </div>

                {/* Method Selection */}
                <div className="mb-6 p-4 bg-surface-muted rounded-lg border border-border">
                  <h4 className="font-semibold text-text-primary mb-3">How do you want to pay off your debts?</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <label className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${selectedMethod === 'wahle-tec' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 text-text-muted'}`}>
                      <input 
                        type="radio" 
                        name="method" 
                        value="wahle-tec" 
                        checked={selectedMethod === 'wahle-tec'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          <img src="/assets/logos/wahle-tec-cog.png" alt="Wahle-Tec" className="h-8 w-8 object-contain" />
                        </div>
                        <div className={`font-semibold ${selectedMethod === 'wahle-tec' ? 'text-primary' : 'text-text-primary'}`}>Wahle-Tec Method™</div>
                        <div className="text-xs text-text-muted mt-1">Recommended</div>
                      </div>
                    </label>
                    
                    <label className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${selectedMethod === 'avalanche' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 text-text-muted'}`}>
                      <input 
                        type="radio" 
                        name="method" 
                        value="avalanche" 
                        checked={selectedMethod === 'avalanche'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <div className="text-2xl mb-2">🔥</div>
                        <div className={`font-semibold ${selectedMethod === 'avalanche' ? 'text-primary' : 'text-text-primary'}`}>Traditional Avalanche</div>
                        <div className="text-xs text-text-muted mt-1">Highest rate first</div>
                      </div>
                    </label>
                    
                    <label className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${selectedMethod === 'snowball' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 text-text-muted'}`}>
                      <input 
                        type="radio" 
                        name="method" 
                        value="snowball" 
                        checked={selectedMethod === 'snowball'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <div className="text-2xl mb-2">❄️</div>
                        <div className={`font-semibold ${selectedMethod === 'snowball' ? 'text-primary' : 'text-text-primary'}`}>Traditional Snowball</div>
                        <div className="text-xs text-text-muted mt-1">Smallest balance first</div>
                      </div>
                    </label>
                    
                    <label className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${selectedMethod === 'cfi' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 text-text-muted'}`}>
                      <input 
                        type="radio" 
                        name="method" 
                        value="cfi" 
                        checked={selectedMethod === 'cfi'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <div className="text-2xl mb-2">💨</div>
                        <div className={`font-semibold ${selectedMethod === 'cfi' ? 'text-primary' : 'text-text-primary'}`}>Cash Flow Index</div>
                        <div className="text-xs text-text-muted mt-1">Free up cash fastest</div>
                      </div>
                    </label>
                    
                    <label className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${selectedMethod === 'custom' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 text-text-muted'}`}>
                      <input 
                        type="radio" 
                        name="method" 
                        value="custom" 
                        checked={selectedMethod === 'custom'}
                        onChange={(e) => {
                          const previousMethod = selectedMethod;
                          setSelectedMethod(e.target.value);
                          if (e.target.checked && customDebtOrder.length === 0) {
                            initializeCustomOrder(previousMethod);
                          }
                        }}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <div className="text-2xl mb-2">🎛️</div>
                        <div className={`font-semibold ${selectedMethod === 'custom' ? 'text-primary' : 'text-text-primary'}`}>Your Own Order</div>
                        <div className="text-xs text-text-muted mt-1">Drag & drop</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Custom Debt Prioritization Interface */}
                {selectedMethod === 'custom' && (
                  <div className="mb-6 p-4 bg-surface-muted rounded-lg border border-border">
                    <h4 className="font-semibold text-text-primary mb-3">🎛️ Custom Debt Prioritization - Drag & Drop to Reorder:</h4>
                    <p className="text-sm text-text-muted mb-4">
                      Drag and drop your debts to set your preferred payoff priority. Debts at the top will be paid off first.
                    </p>
                    <div className="space-y-3">
                      {getSortedDebts().length === 0 ? (
                        <div className="text-center p-8 bg-surface rounded-lg border-2 border-dashed border-border">
                          <div className="text-text-muted mb-4">
                            <div className="text-4xl mb-2">📊</div>
                            <div className="text-lg font-semibold text-text-primary">No Debts to Prioritize</div>
                            <div className="text-sm mt-2">Add some debts first to use custom prioritization</div>
                          </div>
                        </div>
                      ) : getSortedDebts().map((debt, index) => {
                        const currentBalance = getCurrentBalanceForDebt(debt);
                        const isBeingDragged = draggedDebt === debt.id;
                        
                        return (
                          <div
                            key={debt.id}
                            className={`debt-card bg-card border-2 rounded-lg p-4 transition-all cursor-move border-border ${
                              isBeingDragged 
                                ? 'opacity-50 scale-95 border-primary' 
                                : 'hover:border-primary/50 hover:shadow-md'
                            }`}
                            draggable={true}
                            onDragStart={(e) => handleDebtDragStart(e, debt.id)}
                            onDragEnd={handleDebtDragEnd}
                            onDragOver={handleDebtDragOver}
                            onDrop={(e) => handleDebtDrop(e, debt.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <div className="bg-primary/20 text-primary font-bold text-sm px-2 py-1 rounded">
                                      #{index + 1}
                                    </div>
                                    <span className="text-lg text-text-muted">⬍</span>
                                  </div>
                                  <span className="text-xl">{debt.customEmoji}</span>
                                  <div>
                                    <div className="font-semibold text-text-primary">{debt.name}</div>
                                    <div className="text-sm text-text-muted">
                                      Priority #{index + 1}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-text-primary">
                                  ${formatCurrency(currentBalance)}
                                </div>
                                <div className="text-sm text-text-muted">
                                  {(debt.data.annual_rate * 100).toFixed(2)}% APR
                                </div>
                                <div className="text-sm font-medium text-primary mt-1">
                                  ${formatCurrency(calculateMinimumPayment(debt))}/month
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    
                    <div className="mt-4 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">💡 Custom Prioritization Tip:</strong> This custom order will be used in your debt payoff strategy. Changes here will automatically update your payment timeline calculations.
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Algorithm Explanation for Enhanced Wahle-Tec Method */}
                {selectedMethod === 'wahle-tec' && (
                  <div className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                    <h4 className="font-semibold text-primary mb-3">🧮 Enhanced Wahle-Tec Method™ - Advanced Portfolio Analysis:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🚨 Danger Level Classification</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Extreme Danger (≥15%):</strong> 3.0x multiplier</div>
                          <div className="mb-2"><strong>High Danger (≥10%):</strong> 2.0x multiplier</div>
                          <div className="mb-2"><strong>Moderate (≥7%):</strong> 1.5x multiplier</div>
                          <div className="mb-2"><strong>Safe Debt (≤5%):</strong> 0.3x multiplier</div>
                          <div><strong>Credit Cards:</strong> Automatic extreme danger classification</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">💰 Enhanced CFI Foundation</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Base CFI:</strong> Monthly Payment ÷ Months to Payoff</div>
                          <div className="mb-2"><strong>Rate Amplification:</strong> (Rate ÷ Min Rate)^1.5</div>
                          <div className="mb-2"><strong>Portfolio Context:</strong> All calculations relative to debt mix</div>
                          <div><strong>Result:</strong> Higher rates get exponentially higher base scores</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">⚡ Quick Win Strategy</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Small Balance (&lt;40% avg):</strong> +150% boost</div>
                          <div className="mb-2"><strong>Medium Balance (&lt;70% avg):</strong> +100% boost</div>
                          <div className="mb-2"><strong>Psychology:</strong> Momentum through completion</div>
                          <div><strong>Balance Trade-offs:</strong> Smaller debts beat larger despite lower rates</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🎯 High Interest Amplification</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Above Average Rate:</strong> Extra 120% boost</div>
                          <div className="mb-2"><strong>Exponential Scaling:</strong> Extreme rates get massive priority</div>
                          <div><strong>27% Credit Card:</strong> Gets maximum amplification</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🛡️ Low Interest Penalties</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Significantly Below Avg (&lt;60%):</strong> Major penalty</div>
                          <div className="mb-2"><strong>Moderately Below Avg (&lt;80%):</strong> Minor penalty</div>
                          <div className="mb-2"><strong>Mortgage Example:</strong> 3.125% rate gets heavily penalized</div>
                          <div><strong>Result:</strong> "Good debt" ranks appropriately low</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">⚡ Payoff Speed Boost</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Faster Than Average:</strong> +80% boost</div>
                          <div className="mb-2"><strong>Quick Victory:</strong> Maintains momentum</div>
                          <div><strong>Strategic Impact:</strong> Balances time vs. interest cost</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">🎯 Enhanced Priority Score = </strong> 
                        <span className="font-mono text-text-muted">(Enhanced CFI × Danger Multiplier) + High Interest Boost + Quick Win Boost + Payoff Speed Boost - Low Interest Penalty</span>
                        <div className="mt-2 text-xs text-text-muted">
                          This advanced algorithm provides portfolio-relative scoring that properly distinguishes between "dangerous" and "efficient" debt, ensuring optimal prioritization based on your specific debt mix.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Algorithm Explanation for CFI Method */}
                {selectedMethod === 'cfi' && (
                  <div className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                    <h4 className="font-semibold text-primary mb-3">💨 How the Cash Flow Index (CFI) Algorithm Works:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">💰 CFI Formula</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Base Formula:</strong> Monthly Payment ÷ Months to Payoff</div>
                          <div className="mb-2"><strong>Logic:</strong> Higher scores = faster cash flow recovery</div>
                          <div className="mb-2"><strong>Why:</strong> Prioritizes debts that free up monthly cash flow quickest</div>
                          <div><strong>Example:</strong> $500/month debt with 10 months left = CFI of 50</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🎯 Cash Flow Focus</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Primary Goal:</strong> Maximize monthly cash flow recovery</div>
                          <div className="mb-2"><strong>Strategy:</strong> Pay off debts with highest CFI scores first</div>
                          <div className="mb-2"><strong>Benefit:</strong> Freed cash flow accelerates remaining debt payoff</div>
                          <div><strong>Best For:</strong> Tight budgets needing quick payment relief</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">💨 CFI Priority Score = </strong> 
                        <span className="font-mono text-text-muted">Monthly Payment ÷ Months to Payoff</span>
                        <div className="mt-2 text-xs text-text-muted">
                          This method focuses purely on cash flow efficiency, helping you regain monthly payment freedom as quickly as possible.
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">✅ CFI Advantages:</strong> Simple and focused on immediate cash flow relief. Great for budget-constrained situations where freeing up monthly payments is the top priority.
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Algorithm Explanation for Avalanche Method */}
                {selectedMethod === 'avalanche' && (
                  <div className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                    <h4 className="font-semibold text-primary mb-3">🔥 How the Traditional Avalanche Method Works:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">📊 Interest Rate Priority</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Core Strategy:</strong> Pay minimums on all debts</div>
                          <div className="mb-2"><strong>Extra Payment Rule:</strong> Target highest interest rate first</div>
                          <div className="mb-2"><strong>Logic:</strong> Higher rates cost more per dollar over time</div>
                          <div><strong>Goal:</strong> Minimize total interest paid across all debts</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">💰 Mathematical Optimization</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Priority Score:</strong> Interest Rate Percentage</div>
                          <div className="mb-2"><strong>Ranking:</strong> 27% Credit Card beats 4.5% Mortgage</div>
                          <div className="mb-2"><strong>Efficiency:</strong> Interest rate focused approach</div>
                          <div><strong>Savings:</strong> Maximum interest reduction over loan lifetime</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">⚡ Interest Cost Analysis</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>High Rate Impact:</strong> 18%+ rates compound rapidly</div>
                          <div className="mb-2"><strong>Monthly Interest:</strong> Rate ÷ 12 × Balance</div>
                          <div className="mb-2"><strong>Compound Effect:</strong> Interest on unpaid interest</div>
                          <div><strong>Time Factor:</strong> Early elimination prevents compounding</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🎯 Avalanche Strategy Benefits</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Best For:</strong> Disciplined borrowers focused on math</div>
                          <div className="mb-2"><strong>Advantage:</strong> Lowest total interest paid</div>
                          <div className="mb-2"><strong>Timeline:</strong> Fastest debt freedom by total cost</div>
                          <div><strong>Mindset:</strong> Logic over psychology</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">🔥 Avalanche Priority Score = Interest Rate Percentage</strong>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">✅ Avalanche Advantages:</strong> Interest rate focused approach designed to minimize total interest paid. Best choice for borrowers who can stay motivated without quick psychological wins.
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Algorithm Explanation for Snowball Method */}
                {selectedMethod === 'snowball' && (
                  <div className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                    <h4 className="font-semibold text-primary mb-3">⛄ How the Traditional Snowball Method Works:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🎯 Balance Priority</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Core Strategy:</strong> Pay minimums on all debts</div>
                          <div className="mb-2"><strong>Extra Payment Rule:</strong> Target smallest balance first</div>
                          <div className="mb-2"><strong>Logic:</strong> Quick wins build momentum</div>
                          <div><strong>Goal:</strong> Psychological motivation through completion</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🧠 Psychological Optimization</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Priority Score:</strong> Balance Amount (Lower = Higher Priority)</div>
                          <div className="mb-2"><strong>Ranking:</strong> $2,000 card beats $200,000 mortgage</div>
                          <div className="mb-2"><strong>Motivation:</strong> Regular debt elimination celebrations</div>
                          <div><strong>Momentum:</strong> Each payoff increases motivation</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">💪 Momentum Building</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Quick Wins:</strong> First debt paid off fastest</div>
                          <div className="mb-2"><strong>Cash Flow:</strong> Freed payment goes to next debt</div>
                          <div className="mb-2"><strong>Snowball Effect:</strong> Payment amounts grow over time</div>
                          <div><strong>Psychology:</strong> Success breeds success</div>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border border-border">
                        <h5 className="font-semibold text-primary mb-2">🏆 Snowball Strategy Benefits</h5>
                        <div className="text-text-muted">
                          <div className="mb-2"><strong>Best For:</strong> People who need motivation to stick with plan</div>
                          <div className="mb-2"><strong>Advantage:</strong> Highest completion rates</div>
                          <div className="mb-2"><strong>Timeline:</strong> Fastest first debt elimination</div>
                          <div><strong>Mindset:</strong> Psychology over pure math</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">⛄ Snowball Priority Score = Balance Amount (Lower Balance = Higher Priority)</strong>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-primary/10 rounded border-l-4 border-primary">
                      <div className="text-sm text-text-primary">
                        <strong className="text-primary">✅ Snowball Advantages:</strong> Builds momentum through quick wins and psychological satisfaction. Best choice for borrowers who struggle with motivation or have had difficulty sticking to debt payoff plans.
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Debt Analysis Results */}
                <div className="space-y-4">
                  {getSortedDebts().map((debt, index) => {
                    // Get current balance using the centralized function
                    const currentBalance = getCurrentBalanceForDebt(debt);
                    const originalBalance = debt.data.principal || 0;
                    const rate = debt.data.annual_rate || 0;
                    const monthlyInterest = currentBalance * rate / 12;
                    
                    // Get score for current method
                    let score, scoreLabel;
                    switch (selectedMethod) {
                      case 'wahle-tec':
                        score = calculateWahleTecScore(debt);
                        scoreLabel = 'Priority Score';
                        break;
                      case 'avalanche':
                        score = getAvalancheScore(debt) * 100;
                        scoreLabel = 'Interest Rate';
                        break;
                      case 'snowball':
                        score = Math.abs(getSnowballScore(debt));
                        scoreLabel = 'Balance Size';
                        break;
                      case 'cfi':
                        score = getCFIScore(debt);
                        scoreLabel = 'CFI Score';
                        break;
                      default:
                        score = 0;
                        scoreLabel = 'Score';
                    }
                    
                    // Calculate monthly payment using realistic credit card logic
                    let monthlyPayment = 0;
                    if (debt.type === 'credit-card') {
                      // For credit cards, minimum payment must at least cover interest to prevent balance growth
                      monthlyPayment = calculateMinimumPayment(debt);
                    } else {
                      const term = debt.data.term_months || 360;
                      const monthlyRate = rate / 12;
                      if (currentBalance > 0 && rate > 0 && term > 0) {
                        monthlyPayment = currentBalance * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
                      }
                    }
                    
                    // Interest rate categorization (for Wahle-Tec Method)
                    let rateCategory = '';
                    let rateCategoryColor = '';
                    if (selectedMethod === 'wahle-tec') {
                      if (rate <= 0.05) {
                        rateCategory = 'Acceptable';
                        rateCategoryColor = 'text-primary bg-primary/20';
                      } else if (rate <= 0.10) {
                        rateCategory = 'Restructure';
                        rateCategoryColor = 'text-text-muted bg-surface-muted';
                      } else {
                        rateCategory = 'High Priority';
                        rateCategoryColor = 'text-destructive bg-destructive/20';
                      }
                    }
                    
                    // Priority badges
                    const priorityRank = index + 1;
                    const priorityColor = index === 0 ? 'text-primary bg-primary/20' : 
                                        index === 1 ? 'text-primary bg-primary/10' : 
                                        'text-text-muted bg-surface-muted';
                    
                    return (
                      <div key={debt.id} className="border border-border rounded-lg p-4 bg-card">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-text-primary">
                              {debt.type === 'credit-card' ? '💳' : '🏠'} {debt.data.name}
                            </h4>
                            <div className="text-sm text-text-muted">
                              {debt.type === 'credit-card' ? 'Credit Card' : 'Traditional Loan'}
                            </div>
                            {currentBalance !== originalBalance && (
                              <div className="text-xs text-primary mt-1">
                                Balance: ${formatCurrency(originalBalance)} → ${formatCurrency(currentBalance)}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColor}`}>
                              #{priorityRank} Priority
                            </div>
                            {selectedMethod === 'wahle-tec' && (
                              <div className={`px-2 py-1 rounded-full text-xs font-medium mt-1 ${rateCategoryColor}`}>
                                {rateCategory}
                              </div>
                            )}
                            <div className="text-xs text-text-muted mt-1">
                              {scoreLabel}: {scoreLabel === 'Interest Rate' ? `${score.toFixed(2)}%` : 
                                          scoreLabel === 'Balance Size' ? `$${formatCurrency(score)}` : 
                                          scoreLabel === 'CFI Score' ? `$${score.toFixed(2)}` :
                                          score.toFixed(1)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-text-muted">Current Balance:</span>
                            <div className="font-semibold text-text-primary">${formatCurrency(currentBalance)}</div>
                          </div>
                          <div>
                            <span className="text-text-muted">Interest Rate:</span>
                            <div className="font-semibold text-text-primary">{(rate * 100).toFixed(2)}%</div>
                          </div>
                          <div>
                            <span className="text-text-muted">Monthly Interest:</span>
                            <div className="font-semibold text-text-primary">
                              ${formatCurrency(monthlyInterest)}
                            </div>
                          </div>
                          {selectedMethod === 'wahle-tec' && (
                            <div>
                              <span className="text-text-muted">Cash Flow Recovery:</span>
                              <div className="font-semibold text-primary">
                                ${formatCurrency(monthlyPayment)}/month
                              </div>
                              <div className="text-xs text-text-muted">
                                Eliminating this debt recovers this monthly payment
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Formula Breakdown for Wahle-Tec Method */}
                        {selectedMethod === 'wahle-tec' && (
                          <div className="mt-3 p-3 bg-surface-muted rounded-lg border border-border">
                            <h5 className="text-xs font-semibold text-text-primary mb-2">🧮 Priority Score Calculation:</h5>
                            <div className="text-xs text-text-muted space-y-1">
                              {(() => {
                                // Calculate proximity component for display using current balances
                                const maxBalance = Math.max(...debts.map(d => getCurrentBalanceForDebt(d)));
                                const proximityScore = maxBalance > 0 ? ((maxBalance - currentBalance) / maxBalance) * 100 : 0;
                                const proximityComponent = proximityScore * 0.8;
                                
                                // Calculate cash flow recovery using current balances
                                const maxPayment = Math.max(...debts.map(d => {
                                  const dCurrentBalance = getCurrentBalanceForDebt(d);
                                  const dRate = d.data.annual_rate || 0;
                                  const dMonthlyInterest = dCurrentBalance * dRate / 12;
                                  
                                  if (d.type === 'credit-card') {
                                    const dMinimumPercentPayment = dCurrentBalance * (d.data.minimum_payment_percent || 2) / 100;
                                    return Math.max(dMinimumPercentPayment, dMonthlyInterest);
                                  } else {
                                    const dTerm = d.data.term_months || 360;
                                    const dMonthlyRate = dRate / 12;
                                    if (dCurrentBalance > 0 && dRate > 0 && dTerm > 0) {
                                      return dCurrentBalance * (dMonthlyRate * Math.pow(1 + dMonthlyRate, dTerm)) / (Math.pow(1 + dMonthlyRate, dTerm) - 1);
                                    }
                                  }
                                  return 0;
                                }));
                                const cashFlowRecovery = maxPayment > 0 ? (monthlyPayment / maxPayment) * 100 : 0;
                                
                                return (
                                  <>
                                    <div>Cash Flow Recovery: (${formatCurrency(monthlyPayment)} ÷ ${formatCurrency(maxPayment)}) × 100 × 0.4 = {(cashFlowRecovery * 0.4).toFixed(1)}</div>
                                    <div>Interest Component: {(rate * 100).toFixed(2)}% × 300 = {(rate * 100 * 3).toFixed(1)}</div>
                                    <div>Balance Component: ${formatCurrency(currentBalance)} ÷ 1000 × 0.05 = {(currentBalance / 1000 * 0.05).toFixed(1)}</div>
                                    <div>Proximity Component: (${formatCurrency(maxBalance)} - ${formatCurrency(currentBalance)}) ÷ ${formatCurrency(maxBalance)} × 100 × 0.8 = {proximityComponent.toFixed(1)}</div>
                                  </>
                                );
                              })()}
                              <div className="pt-1 border-t border-border font-semibold text-text-primary">
                                Total Priority Score: {score.toFixed(1)}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Formula Breakdown for CFI Method */}
                        {selectedMethod === 'cfi' && (
                          <div className="mt-3 p-3 bg-surface-muted rounded-lg border border-border">
                            <h5 className="text-xs font-semibold text-primary mb-2">💰 Cash Flow Index (CFI) Calculation:</h5>
                            <div className="text-xs text-text-muted space-y-1">
                              {(() => {
                                // Calculate CFI components for display
                                let calculatedMonthlyPayment = 0;
                                if (debt.type === 'credit-card') {
                                  const monthlyInterest = currentBalance * rate / 12;
                                  const percentPayment = currentBalance * (debt.data.minimum_payment_percent || 2) / 100;
                                  calculatedMonthlyPayment = Math.max(percentPayment, monthlyInterest);
                                } else {
                                  // Traditional loan payment calculation
                                  const { principal, annual_rate, term_months } = debt.data;
                                  if (principal > 0 && annual_rate > 0 && term_months > 0) {
                                    const monthlyRate = annual_rate / 12;
                                    const basePayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months)) / 
                                                       (Math.pow(1 + monthlyRate, term_months) - 1);
                                    const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                                                          (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
                                    calculatedMonthlyPayment = basePayment + additionalCosts;
                                  }
                                }
                                
                                // Calculate months to payoff
                                let monthsToPayoff = 0;
                                let balance = currentBalance;
                                const monthlyRate = rate / 12;
                                
                                while (balance > 0.01 && monthsToPayoff < 1000) {
                                  monthsToPayoff++;
                                  const interestPayment = balance * monthlyRate;
                                  const principalPayment = Math.max(0, calculatedMonthlyPayment - interestPayment);
                                  balance -= principalPayment;
                                  if (principalPayment <= 0) break;
                                }
                                
                                const cfiValue = monthsToPayoff > 0 ? (calculatedMonthlyPayment / monthsToPayoff) : 0;
                                const payoffYears = (monthsToPayoff / 12).toFixed(1);
                                
                                return (
                                  <>
                                    <div><strong>Monthly Payment:</strong> ${formatCurrency(calculatedMonthlyPayment)}</div>
                                    <div><strong>Months to Payoff:</strong> {monthsToPayoff} months ({payoffYears} years)</div>
                                    <div><strong>CFI Formula:</strong> Monthly Payment ÷ Months to Payoff</div>
                                    <div><strong>CFI Calculation:</strong> ${formatCurrency(calculatedMonthlyPayment)} ÷ {monthsToPayoff} = ${cfiValue.toFixed(2)}</div>
                                  </>
                                );
                              })()}
                              <div className="pt-1 border-t border-border font-semibold text-text-primary">
                                Cash Flow Index Score: ${score.toFixed(2)}
                              </div>
                              <div className="text-xs text-text-muted mt-1">
                                <strong className="text-primary">Higher CFI = Higher Priority:</strong> This debt generates more cash flow per month of payoff time.
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Formula Breakdown for Avalanche Method */}
                        {selectedMethod === 'avalanche' && (
                          <div className="mt-3 p-3 bg-surface-muted rounded-lg border border-border">
                            <h5 className="text-xs font-semibold text-primary mb-2">🔥 Traditional Avalanche Method Calculation:</h5>
                            <div className="text-xs text-text-muted space-y-1">
                              {(() => {
                                // Calculate total interest savings by eliminating this debt first
                                const totalInterestIfMinimum = currentBalance * rate * (currentBalance / (monthlyPayment * 12)); // Rough estimate
                                const potentialInterestSavings = totalInterestIfMinimum * 0.3; // Estimate 30% savings by paying early
                                
                                // Calculate time to payoff with minimum payments
                                let payoffMonths = 0;
                                let balance = currentBalance;
                                const monthlyRate = rate / 12;
                                
                                while (balance > 0.01 && payoffMonths < 600) {
                                  payoffMonths++;
                                  const interestPayment = balance * monthlyRate;
                                  const principalPayment = Math.max(0, monthlyPayment - interestPayment);
                                  balance -= principalPayment;
                                  if (principalPayment <= 0) break;
                                }
                                
                                const payoffYears = (payoffMonths / 12).toFixed(1);
                                const totalInterestPaid = (monthlyPayment * payoffMonths) - currentBalance;
                                
                                return (
                                  <>
                                    <div>Interest Rate: {(rate * 100).toFixed(2)}% APR (Higher = Higher Priority)</div>
                                    <div>Monthly Interest Cost: ${formatCurrency(monthlyInterest)} per month</div>
                                    <div>Time to Payoff: {payoffMonths} months ({payoffYears} years)</div>
                                    <div>Total Interest if Minimum Payments: ${formatCurrency(totalInterestPaid)}</div>
                                    <div>Potential Interest Savings: ${formatCurrency(potentialInterestSavings)} (by prioritizing)</div>
                                  </>
                                );
                              })()}
                              <div className="pt-1 border-t border-border font-semibold text-text-primary">
                                Avalanche Priority Score: {(rate * 100).toFixed(2)}%
                              </div>
                              <div className="text-xs text-text-muted mt-1">
                                <strong className="text-primary">Math-Optimal Strategy:</strong> Prioritizing highest interest rates minimizes total interest paid across all debts.
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Formula Breakdown for Snowball Method */}
                        {selectedMethod === 'snowball' && (
                          <div className="mt-3 p-3 bg-surface-muted rounded-lg border border-border">
                            <h5 className="text-xs font-semibold text-primary mb-2">⛄ Traditional Snowball Method Calculation:</h5>
                            <div className="text-xs text-text-muted space-y-1">
                              {(() => {
                                // Calculate psychological metrics and payoff timeline
                                const allBalances = debts.map(d => getCurrentBalanceForDebt(d));
                                const totalBalance = allBalances.reduce((sum, bal) => sum + bal, 0);
                                const balancePercentage = (currentBalance / totalBalance) * 100;
                                
                                const payoffMonths = monthlyPayment > 0 ? Math.ceil(currentBalance / monthlyPayment) : 0;
                                const payoffYears = (payoffMonths / 12).toFixed(1);
                                
                                // Calculate momentum metrics
                                const smallerDebts = allBalances.filter(bal => bal < currentBalance).length;
                                const totalDebts = allBalances.length;
                                const psychologicalImpact = ((totalDebts - smallerDebts) / totalDebts * 100).toFixed(1);
                                
                                // Estimate monthly cash flow freed up after payoff
                                const cashFlowRecovered = monthlyPayment;
                                
                                return (
                                  <>
                                    <div>Current Balance: ${formatCurrency(currentBalance)} ({balancePercentage.toFixed(1)}% of total debt)</div>
                                    <div>Monthly Payment: ${formatCurrency(monthlyPayment)}</div>
                                    <div>Time to Payoff: {payoffMonths} months ({payoffYears} years)</div>
                                    <div>Cash Flow Recovery: ${formatCurrency(cashFlowRecovered)}/month after payoff</div>
                                    <div>Psychological Impact: {psychologicalImpact}% completion when eliminated</div>
                                  </>
                                );
                              })()}
                              <div className="pt-1 border-t border-border font-semibold text-text-primary">
                                Snowball Priority Score: ${formatCurrency(Math.abs(score))} (lower balance = higher priority)
                              </div>
                              <div className="text-xs text-text-muted mt-1">
                                <strong className="text-primary">Psychological Strategy:</strong> Quick wins build momentum and motivation for debt elimination journey.
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Recommendations based on priority */}
                        {index === 0 && (
                          <div className="mt-3 p-3 bg-primary/20 rounded-lg border-l-4 border-primary">
                            <div className="text-sm text-text-primary">
                              <strong className="text-primary">🚨 FOCUS HERE:</strong> This debt should receive any extra payments first with the {getMethodInfo().title}
                            </div>
                          </div>
                        )}
                        
                        {index === 1 && (
                          <div className="mt-3 p-3 bg-primary/10 rounded-lg border-l-4 border-primary">
                            <div className="text-sm text-text-primary">
                              <strong className="text-primary">⚠️ NEXT PRIORITY:</strong> Focus here after paying off the #1 priority debt
                            </div>
                          </div>
                        )}
                        
                        {index > 1 && (
                          <div className="mt-3 p-3 bg-surface-muted rounded-lg border-l-4 border-border">
                            <div className="text-sm text-text-muted">
                              <strong>✅ MAINTAIN:</strong> Continue minimum payments while focusing on higher priority debts
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Advanced Strategy Analysis */}
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">
                  🧮 Advanced Debt Optimization Analysis
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="border border-border rounded-lg p-4 bg-surface-muted">
                    <h4 className="font-semibold text-primary mb-2">🎯 Enhanced Wahle-Tec Method™</h4>
                    <div className="text-sm text-text-muted mb-3">
                      Advanced portfolio-relative algorithm with danger classification, exponential rate scaling, and balance-based quick wins.
                    </div>
                    <div className="space-y-2 text-sm text-text-muted">
                      <div className="flex justify-between">
                        <span>Optimization:</span>
                        <span className="font-semibold text-primary">Maximum</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Considers:</span>
                        <span className="font-semibold text-text-primary">6 Advanced Factors</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Debt Classification:</span>
                        <span className="font-semibold text-primary">Danger Levels</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4 bg-surface-muted">
                    <h4 className="font-semibold text-primary mb-2">🔥 Traditional Avalanche</h4>
                    <div className="text-sm text-text-muted mb-3">
                      Simple interest-first approach focusing purely on mathematical optimization to minimize total interest paid.
                    </div>
                    <div className="space-y-2 text-sm text-text-muted">
                      <div className="flex justify-between">
                        <span>Optimization:</span>
                        <span className="font-semibold text-text-primary">Interest Cost</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Considers:</span>
                        <span className="font-semibold text-text-primary">1 Factor</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Psychology:</span>
                        <span className="font-semibold text-text-muted">Not Considered</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4 bg-surface-muted">
                    <h4 className="font-semibold text-primary mb-2">❄️ Traditional Snowball</h4>
                    <div className="text-sm text-text-muted mb-3">
                      Balance-first approach prioritizing psychological wins through completion of smaller debts first.
                    </div>
                    <div className="space-y-2 text-sm text-text-muted">
                      <div className="flex justify-between">
                        <span>Optimization:</span>
                        <span className="font-semibold text-primary">Motivation</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Considers:</span>
                        <span className="font-semibold text-text-primary">1 Factor</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Psychology:</span>
                        <span className="font-semibold text-primary">High Priority</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4 bg-surface-muted">
                    <h4 className="font-semibold text-primary mb-2">💨 Cash Flow Index (CFI)</h4>
                    <div className="text-sm text-text-muted mb-3">
                      Cash flow recovery approach focusing on freeing up monthly payments as quickly as possible.
                    </div>
                    <div className="space-y-2 text-sm text-text-muted">
                      <div className="flex justify-between">
                        <span>Optimization:</span>
                        <span className="font-semibold text-primary">Cash Flow</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Considers:</span>
                        <span className="font-semibold text-text-primary">Payment Speed</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Budget Relief:</span>
                        <span className="font-semibold text-primary">Maximum</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-primary/10 rounded-lg border-l-4 border-primary">
                  <h4 className="font-semibold text-primary mb-2">🔬 Why the Enhanced Wahle-Tec Method™ is Superior:</h4>
                  <ul className="text-sm text-text-muted space-y-2">
                    <li>• <strong>Danger Classification:</strong> Properly categorizes debts by risk level (extreme, high, moderate, safe)</li>
                    <li>• <strong>Portfolio-Relative Scoring:</strong> All calculations adapt to your specific debt mix</li>
                    <li>• <strong>Low-Interest Penalties:</strong> Mortgages and "good debt" appropriately rank lowest</li>
                    <li>• <strong>Enhanced CFI Foundation:</strong> Exponential rate scaling ensures extreme rates get maximum priority</li>
                    <li>• <strong>Quick Win Strategy:</strong> Smaller balances get aggressive boosts for psychological momentum</li>
                    <li>• <strong>Balance vs. Rate Trade-offs:</strong> Truck beats roof despite lower rate due to size advantage</li>
                    <li>• <strong>Credit Card Priority:</strong> Automatic extreme danger classification regardless of balance</li>
                    <li>• <strong>Advanced Weighting:</strong> 6-factor analysis vs. traditional single-factor methods</li>
                  </ul>
                  

                </div>
              </div>

              {/* Cash Flow Impact */}
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">
                  💰 Monthly Cash Flow Analysis
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      ${formatCurrency(debts.reduce((sum, debt) => {
                        const principal = debt.data.principal || 0;
                        const rate = debt.data.annual_rate || 0;
                        const term = debt.data.term_months || 360;
                        const monthlyRate = rate / 12;
                        if (debt.type === 'credit-card') {
                          return sum + calculateMinimumPayment(debt);
                        } else if (principal > 0 && rate > 0 && term > 0) {
                          const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
                          return sum + payment;
                        }
                        return sum;
                      }, 0))}
                    </div>
                    <div className="text-sm text-text-muted">Required Minimum</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      ${formatCurrency(debts.reduce((sum, debt) => sum + (debt.additionalPayments.recurring_extra || 0), 0))}
                    </div>
                    <div className="text-sm text-text-muted">Extra Payments</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text-primary">
                      ${formatCurrency(debts.reduce((sum, debt) => {
                        const principal = debt.data.principal || 0;
                        const rate = debt.data.annual_rate || 0;
                        const term = debt.data.term_months || 360;
                        const monthlyRate = rate / 12;
                        let minPayment = 0;
                        if (debt.type === 'credit-card') {
                          minPayment = principal * (debt.data.minimum_payment_percent || 2) / 100;
                        } else if (principal > 0 && rate > 0 && term > 0) {
                          minPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
                        }
                        return sum + minPayment + (debt.additionalPayments.recurring_extra || 0);
                      }, 0))}
                    </div>
                    <div className="text-sm text-text-muted">Total Monthly</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Debt Planning Tab */}
        {activeTab === 'planning' && (
          <div className="bg-surface-muted p-6 rounded-lg border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-text-primary">
                📊 Comprehensive Debt Payoff Strategy
              </h2>
              <button
                onClick={printDebtPlan}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium"
              >
                🖨️ Print Plan
              </button>
            </div>
            
            {/* Planning Input Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Input Controls */}
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">💰 Your Financial Input</h3>
                
                <div className="space-y-6">
                  {/* Monthly Take-Home Pay */}
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-2">
                      Monthly Take-Home Pay ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={debtPlanningGoals.monthlyIncome || ''}
                      onChange={(e) => setDebtPlanningGoals(prev => ({
                        ...prev,
                        monthlyIncome: parseFloat(e.target.value) || 0
                      }))}
                      placeholder="5200.00"
                      className="w-full px-3 py-2 border border-input rounded-md font-mono bg-background text-foreground"
                    />
                    <div className="text-xs text-text-muted mt-1">
                      Your actual take-home pay after taxes and deductions<br/>
                      <span className="text-primary">💡 Average American household: $5,200/month (based on $83,730 median income)</span>
                    </div>
                  </div>
                  
                  {/* Payment Mode Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-3">
                      Extra Payment Method
                    </label>
                    <div className="flex rounded-lg border border-border bg-surface-muted p-1">
                      <button
                        type="button"
                        onClick={() => setDebtPlanningGoals(prev => ({ ...prev, paymentMode: 'percentage' }))}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          debtPlanningGoals.paymentMode === 'percentage'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-text-muted hover:text-text-primary'
                        }`}
                      >
                        Percentage of Income
                      </button>
                      <button
                        type="button"
                        onClick={() => setDebtPlanningGoals(prev => ({ ...prev, paymentMode: 'amount' }))}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          debtPlanningGoals.paymentMode === 'amount'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-text-muted hover:text-text-primary'
                        }`}
                      >
                        Fixed Dollar Amount
                      </button>
                    </div>
                  </div>
                  
                  {/* Percentage Mode */}
                  {debtPlanningGoals.paymentMode === 'percentage' && (
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-3">
                        Extra Payment Percentage
                      </label>
                      
                      {/* Quick Percentage Buttons */}
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[5, 10, 15, 20].map(percent => (
                          <button
                            key={percent}
                            type="button"
                            onClick={() => setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentPercent: percent
                            }))}
                            className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                              debtPlanningGoals.extraPaymentPercent === percent
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-text-muted border-border hover:border-primary/50 hover:bg-primary/10'
                            }`}
                          >
                            {percent}%
                          </button>
                        ))}
                      </div>
                      
                      {/* Custom Percentage Input */}
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={debtPlanningGoals.extraPaymentPercent || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          const extraPaymentAmount = debtPlanningGoals.monthlyIncome * value / 100;
                          const availableCashFlow = calculateAvailableCashFlow();
                          
                          // Check if extra payment exceeds available cash flow
                          if (extraPaymentAmount > availableCashFlow && availableCashFlow > 0) {
                            const maxPercentage = (availableCashFlow / debtPlanningGoals.monthlyIncome) * 100;
                            setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentPercent: maxPercentage
                            }));
                            alert(`Extra payment has been adjusted to ${maxPercentage.toFixed(1)}% (${formatCurrency(availableCashFlow)}/month). This is your maximum available cash flow after minimum debt payments.`);
                          } else {
                            setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentPercent: value
                            }));
                          }
                        }}
                        placeholder="Custom percentage"
                        className="w-full px-3 py-2 border border-input rounded-md font-mono bg-background text-foreground"
                      />
                      <div className="text-xs text-text-muted mt-1">
                        Or enter a custom percentage of your income
                        {debtPlanningGoals.monthlyIncome > 0 && calculateAvailableCashFlow() > 0 && (
                          <span className="block text-primary mt-1">
                            Available for extra payments: ${formatCurrency(calculateAvailableCashFlow())} 
                            ({((calculateAvailableCashFlow() / debtPlanningGoals.monthlyIncome) * 100).toFixed(1)}% of income)
                          </span>
                        )}
                      </div>
                      
                      {debtPlanningGoals.monthlyIncome > 0 && debtPlanningGoals.extraPaymentPercent > 0 && (
                        <div className={`mt-2 p-2 rounded text-sm ${
                          (() => {
                            const extraAmount = debtPlanningGoals.monthlyIncome * debtPlanningGoals.extraPaymentPercent / 100;
                            const availableCashFlow = calculateAvailableCashFlow();
                            const usagePercentage = availableCashFlow > 0 ? (extraAmount / availableCashFlow) : 0;
                            
                            if (usagePercentage > 0.95) return 'bg-destructive/20 text-destructive border border-destructive/50';
                            if (usagePercentage > 0.85) return 'bg-primary/20 text-primary border border-primary/50';
                            if (usagePercentage > 0.70) return 'bg-primary/10 text-primary border border-border';
                            return 'bg-primary/10 text-text-muted border border-border';
                          })()
                        }`}>
                          <strong>Extra Payment:</strong> ${formatCurrency(debtPlanningGoals.monthlyIncome * debtPlanningGoals.extraPaymentPercent / 100)} per month
                          {(() => {
                            const selectedPercentage = debtPlanningGoals.extraPaymentPercent;
                            const extraAmount = debtPlanningGoals.monthlyIncome * selectedPercentage / 100;
                            const availableCashFlow = calculateAvailableCashFlow();
                            const usagePercentageOfCashFlow = availableCashFlow > 0 ? (extraAmount / availableCashFlow) : 0;
                            
                            if (usagePercentageOfCashFlow > 0.95) {
                              return <div className="text-xs mt-1">🚨 Using {selectedPercentage.toFixed(1)}% of income ({(usagePercentageOfCashFlow * 100).toFixed(0)}% of available cash flow) - very little margin for unexpected expenses</div>;
                            }
                            if (usagePercentageOfCashFlow > 0.85) {
                              return <div className="text-xs mt-1">⚠️ Using {selectedPercentage.toFixed(1)}% of income ({(usagePercentageOfCashFlow * 100).toFixed(0)}% of available cash flow) - aggressive but manageable</div>;
                            }
                            if (usagePercentageOfCashFlow > 0.70) {
                              return <div className="text-xs mt-1">⚡ Using {selectedPercentage.toFixed(1)}% of income ({(usagePercentageOfCashFlow * 100).toFixed(0)}% of available cash flow) - moderate pace</div>;
                            }
                            return <div className="text-xs mt-1">✅ Using {selectedPercentage.toFixed(1)}% of income ({(usagePercentageOfCashFlow * 100).toFixed(0)}% of available cash flow) - comfortable pace</div>;
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Dollar Amount Mode */}
                  {debtPlanningGoals.paymentMode === 'amount' && (
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-3">
                        Extra Payment Amount ($)
                      </label>
                      
                      {/* Quick Amount Buttons */}
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[100, 250, 500, 1000].map(amount => (
                          <button
                            key={amount}
                            type="button"
                            onClick={() => setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentAmount: amount
                            }))}
                            className={`px-2 py-2 text-sm font-medium rounded-md border transition-colors ${
                              debtPlanningGoals.extraPaymentAmount === amount
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-text-muted border-border hover:border-primary/50 hover:bg-primary/10'
                            }`}
                          >
                            ${amount}
                          </button>
                        ))}
                      </div>
                      
                      {/* Custom Amount Input */}
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={debtPlanningGoals.extraPaymentAmount || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          const availableCashFlow = calculateAvailableCashFlow();
                          
                          // Check if extra payment exceeds available cash flow
                          if (value > availableCashFlow && availableCashFlow > 0) {
                            setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentAmount: availableCashFlow
                            }));
                            alert(`Extra payment has been adjusted to $${formatCurrency(availableCashFlow)} per month. This is your maximum available cash flow after minimum debt payments.`);
                          } else {
                            setDebtPlanningGoals(prev => ({
                              ...prev,
                              extraPaymentAmount: value
                            }));
                          }
                        }}
                        placeholder="Custom dollar amount"
                        className="w-full px-3 py-2 border border-input rounded-md font-mono"
                      />
                      <div className="text-xs text-text-muted mt-1">
                        Or enter a custom dollar amount per month
                        {debtPlanningGoals.monthlyIncome > 0 && calculateAvailableCashFlow() > 0 && (
                          <span className="block text-primary mt-1">
                            Available for extra payments: ${formatCurrency(calculateAvailableCashFlow())} 
                            ({((calculateAvailableCashFlow() / debtPlanningGoals.monthlyIncome) * 100).toFixed(1)}% of income)
                          </span>
                        )}
                      </div>
                      
                      {debtPlanningGoals.monthlyIncome > 0 && debtPlanningGoals.extraPaymentAmount > 0 && (
                        <div className={`mt-2 p-2 rounded text-sm ${
                          (() => {
                            const availableCashFlow = calculateAvailableCashFlow();
                            const usagePercentage = availableCashFlow > 0 ? (debtPlanningGoals.extraPaymentAmount / availableCashFlow) : 0;
                            
                            if (usagePercentage > 0.95) return 'bg-destructive/20 text-destructive border border-destructive/50';
                            if (usagePercentage > 0.85) return 'bg-primary/20 text-primary border border-primary/50';
                            if (usagePercentage > 0.70) return 'bg-primary/10 text-primary border border-border';
                            return 'bg-primary/10 text-text-muted border border-border';
                          })()
                        }`}>
                          <strong>Extra Payment:</strong> ${formatCurrency(debtPlanningGoals.extraPaymentAmount)} per month 
                          ({((debtPlanningGoals.extraPaymentAmount / debtPlanningGoals.monthlyIncome) * 100).toFixed(1)}% of income)
                          {(() => {
                            const availableCashFlow = calculateAvailableCashFlow();
                            const usagePercentage = availableCashFlow > 0 ? (debtPlanningGoals.extraPaymentAmount / availableCashFlow) : 0;
                            
                            if (usagePercentage > 0.95) {
                              return <div className="text-xs mt-1">🚨 Using {(usagePercentage * 100).toFixed(0)}% of available cash flow - very little margin for unexpected expenses</div>;
                            }
                            if (usagePercentage > 0.85) {
                              return <div className="text-xs mt-1">⚠️ Using {(usagePercentage * 100).toFixed(0)}% of available cash flow - aggressive but manageable</div>;
                            }
                            if (usagePercentage > 0.70) {
                              return <div className="text-xs mt-1">⚡ Using {(usagePercentage * 100).toFixed(0)}% of available cash flow - moderate pace</div>;
                            }
                            return <div className="text-xs mt-1">✅ Using {(usagePercentage * 100).toFixed(0)}% of available cash flow - comfortable pace</div>;
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Debt Payoff Method Selection - Quick Buttons */}
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-3">
                      Debt Payoff Method
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: 'wahle-tec', name: 'Wahle-Tec Method™', description: 'CFI foundation + portfolio-relative interest protection & easy wins', icon: '🎯' },
                        { value: 'avalanche', name: 'Avalanche Method', description: 'Highest interest rate first (interest rate focused)', icon: '⛰️' },
                        { value: 'snowball', name: 'Snowball Method', description: 'Smallest balance first (psychological wins)', icon: '⛄' },
                        { value: 'cfi', name: 'Cash Flow Index (CFI)', description: 'Fastest cash flow recovery (quickest payment freedom)', icon: '💨' },
                        { value: 'custom', name: 'Custom Prioritization', description: 'Your personalized debt order (drag & drop on Analysis tab)', icon: '🎛️' }
                      ].map(method => (
                        <button
                          key={method.value}
                          type="button"
                          onClick={() => setDebtPlanningGoals(prev => ({
                            ...prev,
                            selectedStrategy: method.value
                          }))}
                          className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                            (debtPlanningGoals.selectedStrategy || selectedMethod) === method.value
                              ? 'border-primary bg-primary/10'
                              : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {method.value === 'wahle-tec' ? (
                              <img src="/assets/logos/wahle-tec-cog.png" alt="Wahle-Tec" className="h-8 w-8 object-contain flex-shrink-0" />
                            ) : (
                              <span className="text-2xl">{method.icon}</span>
                            )}
                            <div>
                              <div className="font-medium text-text-primary">{method.name}</div>
                              <div className="text-sm text-text-muted">{method.description}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Professional Debt Summary */}
              <div className="bg-card p-6 rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-6">
                  <h3 className="text-lg font-semibold text-text-primary">📊 Current Debt Overview</h3>
                  <div className="ml-auto text-xs text-text-muted">
                    {debts.length} {debts.length === 1 ? 'debt' : 'debts'} total
                  </div>
                </div>
                
                {/* Basic Portfolio Metrics */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-text-muted mb-3 uppercase tracking-wide">Portfolio Summary</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {/* Total Balance with Visual Progress */}
                    <div className="bg-surface-muted p-4 rounded-lg border border-border">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-sm font-medium text-text-muted">Total Outstanding Balance</div>
                          <div className="text-2xl font-bold text-text-primary">
                            ${formatCurrency(debts.reduce((sum, debt) => sum + getCurrentBalanceForDebt(debt), 0))}
                          </div>
                        </div>
                        <div className="text-primary">💰</div>
                      </div>
                      {(() => {
                        const totalBalance = debts.reduce((sum, debt) => sum + getCurrentBalanceForDebt(debt), 0);
                        const originalBalance = debts.reduce((sum, debt) => sum + (debt.data.principal || 0), 0);
                        const percentPaid = originalBalance > 0 ? ((originalBalance - totalBalance) / originalBalance) * 100 : 0;
                        return percentPaid > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-text-muted mb-1">
                              <span>Progress: {percentPaid.toFixed(1)}% paid down</span>
                              <span>${formatCurrency(originalBalance - totalBalance)} paid</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-primary h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${Math.min(percentPaid, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Monthly Payment Obligation */}
                    <div className="bg-surface-muted p-4 rounded-lg border border-border">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-sm font-medium text-text-muted">Monthly Payment Obligation</div>
                          <div className="text-xl font-bold text-text-primary">
                            ${formatCurrency(debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0))}
                          </div>
                        </div>
                        <div className="text-primary">📅</div>
                      </div>
                      {debtPlanningGoals.monthlyIncome > 0 && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-text-muted mb-1">
                            <span>Income Usage</span>
                            <span>{((debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0) / debtPlanningGoals.monthlyIncome) * 100).toFixed(1)}% of income</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                ((debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0) / debtPlanningGoals.monthlyIncome) * 100) > 40 
                                  ? 'bg-destructive' 
                                  : ((debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0) / debtPlanningGoals.monthlyIncome) * 100) > 28 
                                    ? 'bg-primary' 
                                    : 'bg-primary'
                              }`}
                              style={{ width: `${Math.min(((debts.reduce((sum, debt) => sum + calculateMinimumPayment(debt), 0) / debtPlanningGoals.monthlyIncome) * 100), 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Interest Rate & Risk Analysis */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-text-muted mb-3 uppercase tracking-wide">Interest Rate Analysis</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-surface-muted p-3 rounded-lg border border-border">
                      <div className="text-xs font-medium text-text-muted mb-1">Average Rate</div>
                      <div className="text-lg font-bold text-text-primary">
                        {(() => {
                          const totalRate = debts.reduce((sum, debt) => sum + (debt.data.annual_rate || 0), 0);
                          return (totalRate / debts.length * 100).toFixed(1);
                        })()}%
                      </div>
                    </div>
                    
                    <div className="bg-surface-muted p-3 rounded-lg border border-border">
                      <div className="text-xs font-medium text-text-muted mb-1">Monthly Interest</div>
                      <div className="text-lg font-bold text-text-primary">
                        ${formatCurrency(debts.reduce((sum, debt) => {
                          const balance = getCurrentBalanceForDebt(debt);
                          const rate = debt.data.annual_rate || 0;
                          return sum + (balance * rate / 12);
                        }, 0))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Minimum Payment Scenario */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-medium text-text-muted mb-3 uppercase tracking-wide flex items-center gap-2">
                    Minimum Payment Scenario
                    <span className="text-xs text-text-muted font-normal lowercase">(no strategy - minimum payments only on each debt)</span>
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-surface-muted p-3 rounded-lg border border-border">
                      <div className="flex justify-between items-center">
                        <div className="text-sm font-medium text-primary">Complete Portfolio Payoff</div>
                        <div className="text-lg font-bold text-text-primary">
                          {(() => {
                            let maxMonths = 0;
                            
                            debts.forEach(debt => {
                              const balance = getCurrentBalanceForDebt(debt);
                              const monthlyPayment = calculateMinimumPayment(debt);
                              let debtPayoffMonths = 0;
                              
                              if (debt.type === 'credit-card' && balance > 0 && monthlyPayment > 0) {
                                const rate = debt.data.annual_rate || 0;
                                const monthlyRate = rate / 12;
                                if (monthlyRate > 0 && monthlyPayment > balance * monthlyRate) {
                                  debtPayoffMonths = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                                } else {
                                  // Handle edge case where minimum payment is not sufficient to cover interest
                                  debtPayoffMonths = 1000; // Assign a very high value indicating this debt won't pay off
                                }
                              } else if (debt.type === 'traditional-loan' || debt.type === 'loan') {
                                // For traditional loans, calculate remaining months based on current balance and first payment date
                                const currentDate = new Date();
                                const firstPaymentDate = new Date(debt.data.first_payment_date || currentDate);
                                const monthsSinceStart = Math.max(0, (currentDate.getFullYear() - firstPaymentDate.getFullYear()) * 12 + 
                                                                    (currentDate.getMonth() - firstPaymentDate.getMonth()));
                                const originalTermMonths = debt.data.term_months || 360;
                                debtPayoffMonths = Math.max(0, originalTermMonths - monthsSinceStart);
                              } else if (debt.type === 'mortgage') {
                                // Handle mortgage type specifically  
                                const currentDate = new Date();
                                const firstPaymentDate = new Date(debt.data.first_payment_date || currentDate);
                                const monthsSinceStart = Math.max(0, (currentDate.getFullYear() - firstPaymentDate.getFullYear()) * 12 + 
                                                                    (currentDate.getMonth() - firstPaymentDate.getMonth()));
                                const originalTermMonths = debt.data.term_months || 360;
                                debtPayoffMonths = Math.max(0, originalTermMonths - monthsSinceStart);
                              }
                              
                              // Ensure we have a valid number
                              if (isNaN(debtPayoffMonths) || debtPayoffMonths < 0) {
                                debtPayoffMonths = 0;
                              }
                              
                              maxMonths = Math.max(maxMonths, debtPayoffMonths);
                            });
                            
                            const years = Math.floor(maxMonths / 12);
                            const months = Math.round(maxMonths % 12);
                            return years > 0 ? `${years}y ${months}m` : `${months}m`;
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-card p-3 rounded-lg border border-border">
                        <div className="text-xs font-medium text-destructive mb-1">Total Interest</div>
                        <div className="text-sm font-bold text-text-primary">
                          ${formatCurrency((() => {
                            return debts.reduce((sum, debt) => {
                              if (debt.type === 'credit-card') {
                                const balance = getCurrentBalanceForDebt(debt);
                                const monthlyPayment = calculateMinimumPayment(debt);
                                const rate = debt.data.annual_rate || 0;
                                const monthlyRate = rate / 12;
                                if (balance > 0 && monthlyRate > 0 && monthlyPayment > balance * monthlyRate) {
                                  const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                                  const totalPaid = monthlyPayment * Math.ceil(months);
                                  return sum + Math.max(0, totalPaid - balance);
                                }
                                return sum + (balance * 0.5);
                              } else if (debt.type === 'traditional-loan' || debt.type === 'loan' || debt.type === 'mortgage') {
                                const originalPrincipal = debt.data.principal || 0;
                                const rate = debt.data.annual_rate || 0;
                                const termMonths = debt.data.term_months || 360;
                                if (originalPrincipal > 0 && rate > 0 && termMonths > 0) {
                                  const monthlyRate = rate / 12;
                                  const basePayment = originalPrincipal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                                                     (Math.pow(1 + monthlyRate, termMonths) - 1);
                                  const totalBasePaid = basePayment * termMonths;
                                  const baseInterest = totalBasePaid - originalPrincipal;
                                  return sum + baseInterest;
                                }
                              }
                              return sum;
                            }, 0);
                          })())}
                        </div>
                      </div>
                      
                      <div className="bg-surface-muted p-3 rounded-lg border border-border">
                        <div className="text-xs font-medium text-primary mb-1">Total Cost</div>
                        <div className="text-sm font-bold text-text-primary">
                          ${formatCurrency((() => {
                            return debts.reduce((sum, debt) => {
                              if (debt.type === 'credit-card') {
                                const balance = getCurrentBalanceForDebt(debt);
                                const monthlyPayment = calculateMinimumPayment(debt);
                                const rate = debt.data.annual_rate || 0;
                                const monthlyRate = rate / 12;
                                if (balance > 0 && monthlyRate > 0 && monthlyPayment > balance * monthlyRate) {
                                  const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                                  return sum + (monthlyPayment * Math.ceil(months));
                                }
                                return sum + (balance * 1.5);
                              } else if (debt.type === 'traditional-loan' || debt.type === 'loan' || debt.type === 'mortgage') {
                                const originalPrincipal = debt.data.principal || 0;
                                const rate = debt.data.annual_rate || 0;
                                const termMonths = debt.data.term_months || 360;
                                if (originalPrincipal > 0 && rate > 0 && termMonths > 0) {
                                  const monthlyRate = rate / 12;
                                  const basePayment = originalPrincipal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                                                     (Math.pow(1 + monthlyRate, termMonths) - 1);
                                  const totalBasePaid = basePayment * termMonths;
                                  const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                                                        (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
                                  const totalAdditionalCosts = additionalCosts * termMonths;
                                  return sum + totalBasePaid + totalAdditionalCosts;
                                }
                              }
                              return sum;
                            }, 0);
                          })())}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Results Section */}
            {debtPayoffStrategy && debtPayoffStrategy.length > 0 && (
              <div className="space-y-6">
                {/* Strategy Overview */}
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-lg font-semibold mb-4 text-text-primary flex items-center gap-2">
                    🎯 Your Debt Freedom Strategy
                    <span className="text-sm font-normal text-text-muted">
                      (Using {
                        // Show the actual method being used in calculations (same logic as calculateDebtPayoffStrategy)
                        (() => {
                          let actualMethod;
                          if (debtPlanningGoals.selectedStrategy) {
                            actualMethod = debtPlanningGoals.selectedStrategy;
                          } else if (selectedMethod === 'custom') {
                            actualMethod = 'custom';
                          } else {
                            actualMethod = selectedMethod;
                          }
                          
                          switch (actualMethod) {
                            case 'custom': return 'Custom Prioritization';
                            case 'wahle-tec': return 'Wahle-Tec Method™';
                            case 'avalanche': return 'Avalanche Method';
                            case 'snowball': return 'Snowball Method';
                            case 'cfi': return 'Cash Flow Index (CFI)';
                            default: return 'Wahle-Tec Method™';
                          }
                        })()
                      })
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="text-2xl font-bold text-primary">
                        {formatTimeDuration(Math.round(debtPayoffStrategy.reduce((total, debt) => Math.max(total, debt.payoffMonth), 0)))}
                      </div>
                      <div className="text-sm text-primary">Time to Freedom</div>
                    </div>
                    
                    <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="text-2xl font-bold text-blue-700">
                        ${formatCurrency(debtPayoffStrategy.reduce((sum, debt) => sum + debt.totalInterestPaid, 0))}
                      </div>
                      <div className="text-sm text-primary">Total Interest Paid</div>
                    </div>
                    
                    <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="text-2xl font-bold text-primary">
                        ${formatCurrency(debtPayoffStrategy.reduce((sum, debt) => sum + debt.totalPaid, 0))}
                      </div>
                      <div className="text-sm text-text-muted">Total Amount Paid</div>
                    </div>
                    
                    <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="text-2xl font-bold text-primary">
                        ${formatCurrency((() => {
                          // Calculate savings using existing calculations
                          
                          // Get total cost from strategy
                          const strategyCost = debtPayoffStrategy.reduce((sum, debt) => sum + (debt.totalPaid || 0), 0);
                          
                          // Get total cost from minimum payments scenario (already calculated above)
                          const minimumPaymentCost = debts.reduce((sum, debt) => {
                            if (debt.type === 'credit-card') {
                              const balance = getCurrentBalanceForDebt(debt);
                              const monthlyPayment = calculateMinimumPayment(debt);
                              const rate = debt.data.annual_rate || 0;
                              const monthlyRate = rate / 12;
                              if (balance > 0 && monthlyRate > 0 && monthlyPayment > balance * monthlyRate) {
                                const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
                                return sum + (monthlyPayment * Math.ceil(months));
                              }
                              return sum + (balance * 1.5);
                            } else if (debt.type === 'traditional-loan' || debt.type === 'loan' || debt.type === 'mortgage') {
                              const originalPrincipal = debt.data.principal || 0;
                              const rate = debt.data.annual_rate || 0;
                              const termMonths = debt.data.term_months || 360;
                              if (originalPrincipal > 0 && rate > 0 && termMonths > 0) {
                                const monthlyRate = rate / 12;
                                const basePayment = originalPrincipal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                                                   (Math.pow(1 + monthlyRate, termMonths) - 1);
                                const totalBasePaid = basePayment * termMonths;
                                const additionalCosts = (debt.data.county_tax || 0) + (debt.data.property_insurance || 0) + 
                                                      (debt.data.pmi || 0) + (debt.data.additional_monthly_costs || 0);
                                const totalAdditionalCosts = additionalCosts * termMonths;
                                return sum + totalBasePaid + totalAdditionalCosts;
                              }
                            }
                            return sum;
                          }, 0);
                          
                          // Calculate total savings
                          const totalSavings = Math.max(0, minimumPaymentCost - strategyCost);
                          return totalSavings;
                        })())}
                      </div>
                      <div className="text-sm text-primary">Total Saved</div>
                    </div>
                  </div>
                  
                  {/* Wealth Building Message */}
                  <div className="bg-gradient-to-r from-green-400 to-green-600 text-white rounded-lg p-6 text-center">
                    <h4 className="text-xl font-bold mb-2">🌟 Your Path to Wealth Building</h4>
                    <p className="text-lg">
                      After {formatTimeDuration(Math.round(debtPayoffStrategy.reduce((total, debt) => Math.max(total, debt.payoffMonth), 0)))},
                      you'll have <strong>${formatCurrency(debtPayoffStrategy.reduce((sum, debt) => sum + debt.monthlyPayment, 0))}</strong> per month 
                      available for investments, emergency fund, or other financial goals!
                    </p>
                    <div className="text-sm mt-2 opacity-90">
                      This represents {((debtPayoffStrategy.reduce((sum, debt) => sum + debt.monthlyPayment, 0) / debtPlanningGoals.monthlyIncome) * 100).toFixed(1)}% 
                      of your current take-home pay that will be freed up for wealth building.
                    </div>
                  </div>
                </div>
                
                {/* Detailed Payoff Timeline */}
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-lg font-semibold mb-4 text-text-primary">📅 Detailed Payoff Timeline</h3>
                  
                  <div className="space-y-6">
                    {debtPayoffStrategy.map((debt, index) => {
                      // Calculate the duration this debt takes to pay off
                      // For sequential strategy: this is the time from when we start focusing on this debt
                      // For our corrected calculation: debt.payoffMonth is the absolute month when paid off
                      const thisDebtDuration = debt.payoffMonth; // This is absolute months from start
                      
                      return (
                        <div key={debt.id} className="border-2 border-border rounded-lg p-5 relative">
                          {/* Timeline Progress Indicator */}
                          <div className="absolute -left-3 top-6 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          
                          <div className="ml-6">
                            {/* Header */}
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <h4 className="font-bold text-lg text-text-primary flex items-center gap-2 mb-1">
                                  {debt.name}
                                  <span className="text-2xl">{debt.type === 'credit-card' ? '💳' : '🏠'}</span>
                                </h4>
                                <div className="text-sm text-text-muted space-y-1">
                                  {debt.method === 'avalanche' && (
                                    <div>🎯 <strong>Priority:</strong> Interest Rate {debt.interestRate.toFixed(2)}% (Highest First)</div>
                                  )}
                                  {debt.method === 'snowball' && (
                                    <div>🎯 <strong>Priority:</strong> Balance ${formatCurrency(debt.startingBalance)} (Smallest First)</div>
                                  )}
                                  {debt.method === 'wahle-tec' && (
                                    <div>🎯 <strong>Priority:</strong> Wahle-Tec Score {debt.priorityScore.toFixed(1)} (Balanced Efficiency)</div>
                                  )}
                                  {debt.method === 'cfi' && (
                                    <div>🎯 <strong>Priority:</strong> CFI Score {debt.priorityScore.toFixed(2)} (Cash Flow Recovery Speed)</div>
                                  )}
                                  <div>💰 <strong>Current Balance:</strong> ${formatCurrency(debt.startingBalance)}</div>
                                  <div>📈 <strong>Interest Rate:</strong> {debt.interestRate.toFixed(2)}% APR</div>
                                </div>
                              </div>
                              
                              <div className="text-right ml-4">
                                <div className="bg-primary/20 text-text-primary px-3 py-1 rounded-lg mb-2">
                                  <div className="text-sm font-medium">Paid Off In</div>
                                  <div className="text-lg font-bold">{formatTimeDuration(thisDebtDuration)}</div>
                                </div>
                                <div className="text-sm text-text-muted">
                                  Month {debt.payoffMonth}
                                </div>
                              </div>
                            </div>
                            
                            {/* Payment Strategy Details */}
                            <div className="bg-surface-muted rounded-lg p-4 mb-4">
                              <h5 className="font-semibold text-text-primary mb-3">💳 Payment Strategy</h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                  <div className="text-sm text-text-muted">Minimum Payment</div>
                                  <div className="font-bold text-text-primary">${formatCurrency(debt.monthlyPayment)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-sm text-text-muted">Extra Applied</div>
                                  <div className="font-bold text-text-primary">${formatCurrency(debt.extraPayment)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-sm text-text-muted">Total Monthly</div>
                                  <div className="font-bold text-text-primary">${formatCurrency(debt.totalMonthlyPayment)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-sm text-text-muted">Interest Saved</div>
                                  <div className="font-bold text-text-primary">
                                    ${formatCurrency(Math.max(0, (debt.startingBalance * debt.interestRate / 100 * thisDebtDuration / 12) - debt.totalInterestPaid))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Financial Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                              <div className="text-center p-3 bg-card rounded-lg border border-border">
                                <div className="text-sm text-destructive">Interest Paid</div>
                                <div className="font-bold text-text-primary">${formatCurrency(debt.totalInterestPaid)}</div>
                              </div>
                              <div className="text-center p-3 bg-surface-muted rounded-lg border border-border">
                                <div className="text-sm text-primary">Total Paid</div>
                                <div className="font-bold text-text-primary">${formatCurrency(debt.totalPaid)}</div>
                              </div>
                              <div className="text-center p-3 bg-surface-muted rounded-lg border border-border">
                                <div className="text-sm text-primary">Cashflow Freed</div>
                                <div className="font-bold text-text-primary">${formatCurrency(debt.monthlyPayment)}</div>
                              </div>
                            </div>
                            
                            {/* Final Freedom Message */}
                            {index === debtPayoffStrategy.length - 1 && (
                              <div className="bg-surface-muted border border-border rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-2xl">🎉</span>
                                  <strong className="text-text-primary">Debt Freedom Achieved!</strong>
                                </div>
                                <div className="text-sm text-primary">
                                  Congratulations! You're now debt-free and have <strong>${formatCurrency(debtPayoffStrategy.reduce((sum, d) => sum + d.monthlyPayment, 0))}</strong> per month 
                                  available for building wealth, emergency fund, or pursuing your financial dreams!
                                </div>
                              </div>
                            )}

                            {/* Snowball Effect Message - For all cards except the last */}
                            {index < debtPayoffStrategy.length - 1 && (
                              <div className="bg-surface-muted border border-border rounded-lg p-4">
                                <div className="text-sm text-text-primary">
                                  Once <strong>{debt.name}</strong> is paid off, the <strong>${formatCurrency(debt.monthlyPayment)}</strong> minimum payment 
                                  will be applied to <strong className="text-primary">{debtPayoffStrategy[index + 1]?.name}</strong> (next priority debt), accelerating your debt freedom!
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Plan Summary */}
                {(() => {
                  const totalMonths = Math.round(debtPayoffStrategy.reduce((total, debt) => Math.max(total, debt.payoffMonth), 0));
                  const totalInterestPaid = debtPayoffStrategy.reduce((sum, debt) => sum + debt.totalInterestPaid, 0);
                  const totalCashfreed = debtPayoffStrategy.reduce((sum, debt) => sum + debt.monthlyPayment, 0);
                  const freedomDate = (() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() + totalMonths);
                    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  })();
                  const totalSaved = (() => {
                    const strategyCost = debtPayoffStrategy.reduce((sum, debt) => sum + (debt.totalPaid || 0), 0);
                    const minimumCost = debts.reduce((sum, debt) => {
                      if (debt.type === 'credit-card') {
                        const balance = getCurrentBalanceForDebt(debt);
                        const payment = calculateMinimumPayment(debt);
                        const rate = debt.data.annual_rate || 0;
                        const monthlyRate = rate / 12;
                        if (balance > 0 && monthlyRate > 0 && payment > balance * monthlyRate) {
                          const months = -Math.log(1 - (balance * monthlyRate) / payment) / Math.log(1 + monthlyRate);
                          return sum + (payment * Math.ceil(months));
                        }
                        return sum + (balance * 1.5);
                      } else if (debt.type === 'traditional-loan' || debt.type === 'loan' || debt.type === 'mortgage') {
                        const principal = debt.data.principal || 0;
                        const rate = debt.data.annual_rate || 0;
                        const termMonths = debt.data.term_months || 360;
                        if (principal > 0 && rate > 0 && termMonths > 0) {
                          const mr = rate / 12;
                          const pmt = principal * (mr * Math.pow(1 + mr, termMonths)) / (Math.pow(1 + mr, termMonths) - 1);
                          return sum + pmt * termMonths;
                        }
                      }
                      return sum;
                    }, 0);
                    return Math.max(0, minimumCost - strategyCost);
                  })();

                  return (
                    <div className="bg-card border border-border rounded-lg p-6">
                      <h3 className="text-lg font-semibold mb-4 text-text-primary flex items-center gap-2">
                        📊 Plan Summary
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                          <div className="text-2xl font-bold text-primary">{formatTimeDuration(totalMonths)}</div>
                          <div className="text-sm text-text-muted mt-1">Time to Debt Freedom</div>
                        </div>
                        <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                          <div className="text-2xl font-bold text-green-600">${formatCurrency(totalSaved)}</div>
                          <div className="text-sm text-text-muted mt-1">Interest Saved vs. Minimums</div>
                        </div>
                        <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                          <div className="text-2xl font-bold text-primary">${formatCurrency(totalCashfreed)}/mo</div>
                          <div className="text-sm text-text-muted mt-1">Monthly Cashflow Freed</div>
                        </div>
                        <div className="text-center p-4 bg-surface-muted rounded-lg border border-border">
                          <div className="text-2xl font-bold text-primary">{freedomDate}</div>
                          <div className="text-sm text-text-muted mt-1">Projected Freedom Date</div>
                        </div>
                      </div>
                      <div className="mt-4 text-center text-sm text-text-muted">
                        Total interest paid: <strong className="text-destructive">${formatCurrency(totalInterestPaid)}</strong> &nbsp;|&nbsp;
                        Total paid across all debts: <strong className="text-text-primary">${formatCurrency(debtPayoffStrategy.reduce((sum, debt) => sum + debt.totalPaid, 0))}</strong>
                      </div>
                    </div>
                  );
                })()}

                {/* Supercharge Your Debt Payoff Section */}
                <div className="bg-surface-muted border border-border rounded-lg p-6 mt-8">
                  <h3 className="text-2xl font-semibold mb-6 text-text-primary flex items-center gap-2">
                    <span>🚀</span>
                    How to Supercharge Your Debt Payoff Plan
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Triple Paycheck Strategy */}
                    <div className="bg-card rounded-lg p-5 border border-border">
                      <h4 className="text-lg font-semibold mb-3 text-text-primary flex items-center gap-2">
                        <span>💰</span>
                        1. Use Your "Bonus Paycheck Months"
                      </h4>
                      <div className="space-y-3 text-text-muted">
                        <p>If you're paid every two weeks, there are two months each year where you actually get three paychecks instead of two.</p>
                        <p>Since your budget is based on two paychecks, that third one is "extra."</p>
                        <p>Instead of letting it vanish, throw the entire paycheck at your debt.</p>
                        
                        {/* Calculate and display triple paycheck months */}
                        <div className="bg-surface-muted border border-border rounded-lg p-4 mt-4">
                          <h5 className="font-semibold text-text-primary mb-2">🗓️ Your {new Date().getFullYear()} Triple Paycheck Months:</h5>
                          <div className="text-primary">
                            {(() => {
                              // Calculate triple paycheck months for current year
                              // For bi-weekly pay (every 14 days), there are 26 pay periods per year
                              // Most months have 2 paychecks, but 2 months will have 3
                              
                              const currentYear = new Date().getFullYear();
                              
                              // For 2025, most common patterns:
                              // If first paycheck is Jan 3: March 28, June 27, September 26, December 26 (4 months - rare)
                              // If first paycheck is Jan 10: March 7, May 30, August 29, November 28 (4 months - rare)  
                              // Most commonly it's 2 months: March & August OR May & October
                              
                              let triplePaycheckMonths;
                              if (currentYear === 2025) {
                                triplePaycheckMonths = ['March', 'August'];
                              } else {
                                // General approximation for other years
                                triplePaycheckMonths = ['March', 'August'];
                              }
                              
                              return (
                                <div>
                                  <p className="font-medium">
                                    Most likely: <strong>{triplePaycheckMonths[0]} {currentYear}</strong> and <strong>{triplePaycheckMonths[1]} {currentYear}</strong>
                                  </p>
                                  <p className="text-sm mt-1">
                                    (Exact months depend on your pay start date - check with your payroll department)
                                  </p>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="bg-surface-muted border-l-4 border-primary p-3 mt-3">
                          <p className="text-text-primary">
                            <strong>💡 Smart tip:</strong> Target a smaller debt with that big payment. Wiping out one debt frees up its monthly payment, which you can then redirect toward the others. This creates a snowball effect but much faster.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Pause Strategy */}
                    <div className="bg-card rounded-lg p-5 border border-border">
                      <h4 className="text-lg font-semibold mb-3 text-text-primary flex items-center gap-2">
                        <span>⏸️</span>
                        2. Ask for a "Payment Pause" (Forbearance)
                      </h4>
                      <div className="space-y-3 text-text-muted">
                        <p>Many creditors will let you skip up to two payments per year if you ask — this is called a forbearance.</p>
                        <p>Most people don't know this option exists.</p>
                        <p>If you time your forbearance during a three-paycheck month, you can pile all of that "freed-up" money onto a single debt.</p>
                        
                        <div className="bg-surface-muted border-l-4 border-primary p-3 mt-3">
                          <p className="text-text-primary">
                            <strong>💡 Smart tip:</strong> Combining a forbearance with your extra paycheck can sometimes let you pay off a whole debt at once, freeing up even more monthly cash flow.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Why This Works */}
                    <div className="bg-gradient-to-r bg-surface-muted rounded-lg p-5 border border-green-200">
                      <h4 className="text-lg font-semibold mb-3 text-text-primary flex items-center gap-2">
                        <span>⚡</span>
                        Why This Works
                      </h4>
                      <div className="text-primary space-y-2">
                        <p>Debt freedom isn't just about paying — it's about strategically freeing up cash flow.</p>
                        <p>Each time you knock out a debt, you get more money to attack the next one.</p>
                        <p className="font-medium">Done right, these little "hacks" can take <strong>years</strong> off your debt payoff timeline.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Amortization Schedule Display */}
      {amortizationResult && (
        <div className="bg-card border rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">📅 Amortization Schedule</h3>
          
          <div className="mb-4 p-4 bg-surface-muted rounded">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-text-muted">Loan Name:</span>
                <div className="font-semibold">{amortizationResult.loan.name}</div>
              </div>
              <div>
                <span className="text-text-muted">Total Payments:</span>
                <div className="font-semibold">${amortizationResult.summary.total_payments}</div>
              </div>
              <div>
                <span className="text-text-muted">Total Interest:</span>
                <div className="font-semibold text-destructive">${amortizationResult.summary.total_interest}</div>
              </div>
              <div>
                <span className="text-text-muted">Payoff Date:</span>
                <div className="font-semibold">{amortizationResult.summary.payoff_date}</div>
              </div>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Payment</th>
                  <th className="px-3 py-2 text-right">Interest</th>
                  <th className="px-3 py-2 text-right">Principal</th>
                  <th className="px-3 py-2 text-right">Extra</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {amortizationResult.schedule.map((payment, index) => (
                  <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-card' : 'bg-surface-muted'}`}>
                    <td className="px-3 py-2">{payment.payment_number}</td>
                    <td className="px-3 py-2">{payment.date}</td>
                    <td className="px-3 py-2 text-right">${payment.payment_amount}</td>
                    <td className="px-3 py-2 text-right text-destructive">${payment.interest}</td>
                    <td className="px-3 py-2 text-right text-primary">${payment.principal}</td>
                    <td className="px-3 py-2 text-right text-primary">${payment.additional_principal}</td>
                    <td className="px-3 py-2 text-right font-medium">${payment.remaining_balance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </div>

      {/* Debt Type Selection Modal */}
      {showDebtTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-8 max-w-md w-full mx-4 shadow-xl space-y-4">
            <h2 className="text-2xl font-bold mb-6 text-text-primary text-center">
              Choose Debt Type
            </h2>
            <button
              onClick={createTraditionalLoan}
                className="w-full p-6 bg-surface-muted hover:bg-primary/10 border-2 border-primary rounded-lg transition-colors duration-200"
              >
                <div className="text-4xl mb-2">🏠</div>
                <div className="text-lg font-semibold text-text-primary">Traditional Loan</div>
                <div className="text-sm text-primary mt-1">
                  Mortgage, auto loan, personal loan with fixed terms
                </div>
              </button>
              
              <button
                onClick={createCreditCard}
                className="w-full p-6 bg-surface-muted hover:bg-primary/10 border-2 border-primary rounded-lg transition-colors duration-200"
              >
                <div className="text-4xl mb-2">💳</div>
                <div className="text-lg font-semibold text-text-primary">Credit Card</div>
                <div className="text-sm text-primary mt-1">
                  Revolving credit with utilization tracking and strategies
                </div>
              </button>
            
            <button
              onClick={() => setShowDebtTypeModal(false)}
              className="w-full mt-6 px-4 py-2 bg-surface-muted hover:bg-surface text-text-muted rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Debt Customize Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-8 max-w-md w-full mx-4 shadow-xl space-y-6">
            <h2 className="text-2xl font-bold mb-6 text-text-primary text-center">
              Customize Debt
            </h2>
            {/* Name Input */}
            <div>
                <label className="block text-sm font-medium text-text-muted mb-2">
                  Debt Name
                </label>
                <input
                  type="text"
                  value={modalDebtName}
                  onChange={(e) => setModalDebtName(e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter debt name"
                  autoFocus
                />
              </div>

              {/* Emoji Selection */}
              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">
                  Emoji
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={modalDebtEmoji}
                    onChange={(e) => setModalDebtEmoji(e.target.value)}
                    className="w-16 px-3 py-3 border border-input rounded-lg text-center text-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="🏠"
                  />
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="px-4 py-3 bg-surface-muted border border-input rounded-lg hover:bg-surface transition-colors text-foreground font-medium"
                    title="Choose emoji"
                  >
                    😊 Choose
                  </button>
                </div>
                
                {/* Emoji Picker in Modal */}
                {showEmojiPicker && (
                  <div className="mt-4 border border-border rounded-lg overflow-hidden emoji-picker-react">
                    <EmojiPicker
                      theme="dark"
                      onEmojiClick={(emojiObject) => {
                        const emoji = emojiObject.emoji;
                        setModalDebtEmoji(emoji);
                        setShowEmojiPicker(false);
                      }}
                      width={400}
                      height={350}
                      autoFocusSearch={false}
                    />
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeCustomizeModal}
                  className="flex-1 px-4 py-3 bg-muted text-muted-foreground rounded-lg hover:opacity-90 transition-opacity font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCustomization}
                  className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium"
                >
                  Save Changes
                </button>
              </div>
          </div>
        </div>
      )}
      </div></ToolLayout>);
};

export default DebtManagementTest;